"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  User,
  Briefcase,
  Building2,
  TrendingUp,
  Filter,
  Trash2,
  Plus,
  Grid,
  List,
  Lightbulb,
  Award,
  FileUp,
  Info,
  Check,
  Copy,
  X,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronLeft,
  ArrowUp,
  ArrowDown,
  ShieldCheck,
  Send,
  Eye,
} from "lucide-react";
import Modal from "../layout/Modal";
import MessageManager from "../layout/Message";
import BulkAction from "../shared/utils/bulkAction";
import Pagination from "../shared/paginator/paginator";

import NoDataFound from "../shared/NoDataFound.js";
import EmptyState from "../shared/EmptyState.js";
import FilterNoResults from "../shared/FilterNoResults.js";
import ViewMoreModal from "../shared/ViewMoreModal";
import {
  getPeopleAccess,
  getLoggedInUserRole,
  ROLES,
} from "../shared/access.js";
import "./EmployeesPage.css";
import {
  formatEmployeeCreatedAt,
  handleApiError as baseHandleApiError,
} from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import CustomTooltip from "../shared/CustomTooltip.js";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import BackButton from "../shared/backButton";
import {
  readPartnerCollaborationState,
  subscribePartnerCollaborationState,
} from "../shared/utils/partnerCollaborationState";
import {
  getAllPeopleApi,
  getPeopleInviteLink,
  sendReminderEmailApi,
  removePeopleFromOrgApi,
  downloadPeopleDataApi,
  bulkEditPeopleDepartmentApi,
  shareWithPartnersApi,
} from "../../services/peopleService";
import { getDepartmentsListApi } from "../../services/settingsService";
import { getPartnersListApi } from "../../services/hiringService";
import { useTranslation } from "react-i18next";
// import NoDataFound from "../shared/NoDataFound";

const SkeletonListRow = () => {
  const { t } = useTranslation();
  return (
    <div className="list-row skeleton-list-row">
      <div className="list-col">
        <div className="employee-name-cell">
          <div className="employee-avatar-small skeleton-avatar" />
          <div className="skeleton-line short" />
        </div>
      </div>
      <div className="list-col">
        <div className="skeleton-line" />
      </div>
      <div className="list-col">
        <div className="skeleton-line" />
      </div>
      <div className="list-col">
        <div className="skeleton-badge" />
      </div>
      <div className="list-col">
        <div className="skeleton-badge" />
      </div>
      {/* New skeleton for Assessment Progress */}
      <div className="list-col">
        <div className="skeleton-assessment-progress">
          <div className="skeleton-assessment-step" />
          <div className="skeleton-progress-line" /> {/* Skeleton for line */}
          <div className="skeleton-assessment-step" />
          <div className="skeleton-progress-line" /> {/* Skeleton for line */}
          <div className="skeleton-assessment-step" />
        </div>
      </div>
      <div className="list-col">
        <div className="skeleton-icon-btn" />
      </div>
    </div>
  );
};
const SkeletonCard = () => {
  const { t } = useTranslation();
  return (
    <div className="employee-card skeleton-card">
      <div className="card-header">
        <div className="employee-avatar skeleton-avatar" />
        <div className="skeleton-line short" />
        <div className="skeleton-line" />
      </div>
      <div className="employee-details">
        <div className="skeleton-line" />
        <div className="skeleton-line" />
      </div>
      <div className="em-skills-section">
        <div className="skeleton-line short" />
        <div className="skeleton-tags">
          <div className="skeleton-tag" />
          <div className="skeleton-tag" />
          <div className="skeleton-tag" />
        </div>
      </div>
      {/* New skeleton for Assessment Progress */}
      <div className="mt-4">
        <div className="skeleton-line short" />
        <div className="skeleton-assessment-progress mt-2">
          <div className="skeleton-assessment-step" />
          <div className="skeleton-progress-line" /> {/* Skeleton for line */}
          <div className="skeleton-assessment-step" />
          <div className="skeleton-progress-line" /> {/* Skeleton for line */}
          <div className="skeleton-assessment-step" />
        </div>
      </div>
    </div>
  );
};

const isAssessmentComplete = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) return false;

    const numericValue = Number(normalizedValue);
    if (Number.isFinite(numericValue)) return numericValue > 0;

    return ["completed", "complete", "uploaded", "true"].includes(
      normalizedValue,
    );
  }

  return false;
};

const getSharedPartners = (profile) =>
  Array.from(
    new Set(
      [
        ...(Array.isArray(profile?.partners) ? profile.partners : []),
        ...(Array.isArray(profile?.sharedWithPartners)
          ? profile.sharedWithPartners
          : []),
      ]
        .map((partner) =>
          typeof partner === "string"
            ? partner
            : partner?.partnerOrganization || partner?.name,
        )
        .map((partner) => String(partner || "").trim())
        .filter(Boolean),
    ),
  );

const EmployeesPage = () => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const location = useLocation();

  // State for the new smart filter panel
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  // Existing state for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [invitePeopleDepartment, setInvitePeopleDepartment] = useState("");
  const [bulkEditDepartment, setBulkEditDepartment] = useState("");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterAvailability, setFilterAvailability] = useState("All");
  const [searchParams] = useSearchParams();
  const peopleAccess = getPeopleAccess();
  const loggedInRole = getLoggedInUserRole();
  const showDisabledInviteTooltip =
    !peopleAccess.canInvitePeople && loggedInRole === ROLES.GUEST;
  const showDisabledDeletePeopleTooltip =
    !peopleAccess.canDeleteUsers &&
    (loggedInRole === ROLES.MANAGER || loggedInRole === ROLES.GUEST);
  const permissionDeniedMessage = t("common.permissions.contactAdminForAction");
  const readSprintStatusFromUrl = () => {
    const raw = searchParams.get("sprintStatus");

    if (!raw) return "All";

    const value = raw.trim().toLowerCase();

    if (value === "completed") return "Completed";
    if (value === "pending") return "Pending";
    if (value === "all") return "All";

    return raw;
  };

  const [filterSprintsCompleted, setFilterSprintsCompleted] = useState(
    readSprintStatusFromUrl,
  );
  // const [filterSprintsCompleted, setFilterSprintsCompleted] = useState("All"); // New filter state
  const [filterCareerProfileStatus, setFilterCareerProfileStatus] =
    useState("All"); // New filter state
  const [filterResumeStatus, setFilterResumeStatus] = useState("All"); // New filter state
  const [filterSharedWith, setFilterSharedWith] = useState("All");
  const [sortBy, setSortBy] = useState("Name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [tableSortBy, setTableSortBy] = useState(null);
  const [tableSortOrder, setTableSortOrder] = useState("asc");
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    employeeId: null,
  });
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [showBulkDepartmentEdit, setShowBulkDepartmentEdit] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [partnerCollaboration, setPartnerCollaboration] = useState(
    readPartnerCollaborationState,
  );

  useEffect(() => {
    return subscribePartnerCollaborationState(setPartnerCollaboration);
  }, []);

  // External Profile UI State
  const [profilesActiveView, setProfilesActiveView] = useState("internal");
  const showExternalProfilesView = profilesActiveView === "external";
  const setShowExternalProfilesView = (nextValue) =>
    setProfilesActiveView(nextValue ? "external" : "internal");
  const [externalSearchTerm, setExternalSearchTerm] = useState("");
  const [externalSearchInputValue, setExternalSearchInputValue] = useState("");
  const [showExternalSearchBar, setShowExternalSearchBar] = useState(false);
  const [showExternalFiltersPanel, setShowExternalFiltersPanel] =
    useState(false);
  const [externalSelectedIds, setExternalSelectedIds] = useState([]);
  const [externalPartnerFilter, setExternalPartnerFilter] =
    useState("All Partners");
  const [externalAvailabilityFilter, setExternalAvailabilityFilter] =
    useState("All Availability");
  const [externalActionModalOpen, setExternalActionModalOpen] = useState(false);
  const [selectedExternalProfile, setSelectedExternalProfile] = useState(null);
  const [externalActionMessage, setExternalActionMessage] = useState("");
  const [externalProfileModalOpen, setExternalProfileModalOpen] =
    useState(false);
  const [sharePartnerModalOpen, setSharePartnerModalOpen] = useState(false);
  const [sharePartnerSelection, setSharePartnerSelection] = useState("");
  const [sharePartnerProfileIds, setSharePartnerProfileIds] = useState([]);
  const [sharedPartnersModal, setSharedPartnersModal] = useState({
    employeeName: "",
    partners: [],
  });
  const sharePartnerPickerRef = useRef(null);

  const [sharePartnerSearch, setSharePartnerSearch] = useState("");

  const [showSharePartnerResults, setShowSharePartnerResults] = useState(false);

  const [eligibleProfileSharePartners, setEligibleProfileSharePartners] =
    useState([]);

  const [isProfileSharePartnersLoading, setIsProfileSharePartnersLoading] =
    useState(false);

  const [profileSharePartnersError, setProfileSharePartnersError] =
    useState("");
  const [externalProfiles, setExternalProfiles] = useState([
    {
      id: "EXT-1042",
      candidateName: "Tanya Bhatia",
      candidateEmail: "tanya.bhatia@example.com",
      profileStatus: "Questions, Skill Profile, Resume completed",
      canViewCandidateDetails: true,
      partner: "Apex Talent Partners",
      role: "Backend Developer",
      department: "Engineering",
      availability: "Available",
      status: "Ready to Review",
      completedQuestionsCount: 45,
      mayamayaAssessment: {
        quizCompleted: true,
        skillAssessmentCompleted: true,
        resumeUploadCompleted: true,
      },
    },
    {
      id: "EXT-1187",
      candidateName: "Rahul Mehta",
      candidateEmail: "rahul.mehta@example.com",
      profileStatus: "Questions and Skill Profile completed, Resume pending",
      canViewCandidateDetails: false,
      partner: "GlobalStaff Solutions",
      role: "Data Engineer",
      department: "Data Science",
      availability: "Pending Confirmation",
      status: "Availability Requested",
      completedQuestionsCount: 27,
      mayamayaAssessment: {
        quizCompleted: true,
        skillAssessmentCompleted: true,
        resumeUploadCompleted: false,
      },
    },
    {
      id: "EXT-2210",
      candidateName: "Neha Sharma",
      candidateEmail: "neha.sharma@example.com",
      profileStatus: "Profile incomplete",
      canViewCandidateDetails: false,
      partner: "NorthBridge Talent",
      role: "Documentation Specialist",
      department: "Knowledge Management",
      availability: "Unknown",
      status: "Needs Confirmation",
      completedQuestionsCount: 0,
      mayamayaAssessment: {
        quizCompleted: false,
        skillAssessmentCompleted: false,
        resumeUploadCompleted: false,
      },
    },
  ]);

  const hasActivePartnerConnection = externalProfiles.some((profile) => {
    const partnerName = String(profile.partner || "").trim();
    const status = String(
      profile.partnerConnectionStatus || profile.connectionStatus || "Active",
    ).toLowerCase();

    return partnerName && ["active", "accepted", "connected"].includes(status);
  });
  const hasSharedExternalProfiles =
    hasActivePartnerConnection && externalProfiles.length > 0;

  const showSharedWithColumn = Boolean(partnerCollaboration?.enabled);

  useEffect(() => {
    if (!hasActivePartnerConnection && profilesActiveView === "external") {
      setProfilesActiveView("internal");
    }
  }, [hasActivePartnerConnection, profilesActiveView]);

  const [savedProfileViews, setSavedProfileViews] = useState([
    {
      id: "sv-1",
      name: "Apex Profiles to Review",
      internalIds: [],
      externalIds: ["EXT-1042"],
      updatedAt: "Today",
    },
    {
      id: "sv-2",
      name: "Healthcare Staffing Shortlist",
      internalIds: ["EMP-1001", "EMP-1004"],
      externalIds: ["EXT-1042", "EXT-1187"],
      updatedAt: "Yesterday",
    },
    {
      id: "sv-3",
      name: "Data Engineer Candidates",
      internalIds: ["EMP-1002"],
      externalIds: ["EXT-1187"],
      updatedAt: "2 days ago",
    },
    {
      id: "sv-4",
      name: "Q3 Leadership Review",
      internalIds: ["EMP-1001", "EMP-1003", "EMP-1004"],
      externalIds: [],
      updatedAt: "Last week",
    },
  ]);
  const [savedViewSearchTerm, setSavedViewSearchTerm] = useState("");
  const [selectedSavedView, setSelectedSavedView] = useState(null);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [pendingSavedViewSelection, setPendingSavedViewSelection] = useState({
    internalIds: [],
    externalIds: [],
  });

  const savedViewDummyInternalProfiles = [
    {
      id: "EMP-1001",
      name: "Aarav Sharma",
      role: "Senior Product Manager",
      department: "Product",
      availability: "Available",
    },
    {
      id: "EMP-1002",
      name: "Meera Iyer",
      role: "Data Engineer",
      department: "Data Science",
      availability: "Available",
    },
    {
      id: "EMP-1003",
      name: "Rohan Kapoor",
      role: "Engineering Lead",
      department: "Engineering",
      availability: "Not Available",
    },
    {
      id: "EMP-1004",
      name: "Nisha Menon",
      role: "Healthcare Operations Analyst",
      department: "Operations",
      availability: "Available",
    },
  ];

  const getProfileId = (profile) =>
    profile.profileId ||
    profile.employeeId ||
    profile.userId ||
    profile._id ||
    profile.id ||
    "-";

  const getSharedWithText = (profile) => {
    const sharedPartners = getSharedPartners(profile);
    return sharedPartners.length ? sharedPartners.join(", ") : "Not shared";
  };

  const closeSharedPartnersModal = () =>
    setSharedPartnersModal({ employeeName: "", partners: [] });

  const openSharedPartnersModal = (profile) => {
    setSharedPartnersModal({
      employeeName: profile?.name || getProfileId(profile),
      partners: getSharedPartners(profile),
    });
  };

  const renderSharedWithCell = (employee) => {
    const sharedPartners = getSharedPartners(employee);

    if (!sharedPartners.length) {
      return <span>Not shared</span>;
    }

    if (sharedPartners.length <= 2) {
      return (
        <span title={sharedPartners.join(", ")}>
          {sharedPartners.join(", ")}
        </span>
      );
    }

    return (
      <>
        <span title={sharedPartners.join(", ")}>
          {sharedPartners.slice(0, 2).join(", ")}
        </span>
        <button
          type="button"
          className="shared-with-more-btn"
          onClick={(event) => {
            event.stopPropagation();
            openSharedPartnersModal(employee);
          }}
        >
          +{sharedPartners.length - 2} more
        </button>
      </>
    );
  };

  const sharedWithPartnerOptions = useMemo(
    () =>
      Array.from(
        new Set(employees.flatMap((employee) => getSharedPartners(employee))),
      ).filter(Boolean),
    [employees],
  );

  const loadEligibleProfileSharePartners = async () => {
    try {
      setIsProfileSharePartnersLoading(true);
      setProfileSharePartnersError("");
      setEligibleProfileSharePartners([]);

      const response = await getPartnersListApi();

      console.log("Complete getPartnersListApi response:", response);

      let responseData = [];

      // API/service directly returned an array.
      if (Array.isArray(response)) {
        responseData = response;
      }
      // Axios/service response.data is an array.
      else if (Array.isArray(response?.data)) {
        responseData = response.data;
      }
      // Response: { data: { partners: [] } }
      else if (Array.isArray(response?.data?.partners)) {
        responseData = response.data.partners;
      }
      // Response: { data: { items: [] } }
      else if (Array.isArray(response?.data?.items)) {
        responseData = response.data.items;
      }
      // Response: { data: { data: [] } }
      else if (Array.isArray(response?.data?.data)) {
        responseData = response.data.data;
      }
      // Response: { partners: [] }
      else if (Array.isArray(response?.partners)) {
        responseData = response.partners;
      }
      // Response: { items: [] }
      else if (Array.isArray(response?.items)) {
        responseData = response.items;
      }

      console.log("Extracted partner array:", responseData);

      const normalizedPartners = responseData
        .filter((partner) => {
          const normalizedStatus = String(partner?.status || "")
            .trim()
            .toUpperCase();

          const sharedData = Array.isArray(partner?.sharedData)
            ? partner.sharedData
            : [];

          const canShareProfiles = sharedData.some(
            (item) =>
              String(item || "")
                .trim()
                .toLowerCase() === "profiles",
          );

          return normalizedStatus === "ACTIVE" && canShareProfiles;
        })
        .map((partner, index) => ({
          id: partner?._id || partner?.id || `partner-${index}`,

          name:
            partner?.partnerOrganization ||
            partner?.organizationName ||
            partner?.name ||
            "Unnamed organization",

          orgCode:
            partner?.partnerOrgCode ||
            partner?.organizationCode ||
            partner?.orgCode ||
            "",

          admin:
            partner?.partnerSuperAdminName ||
            partner?.adminName ||
            partner?.contactName ||
            "",

          email:
            partner?.partnerSuperAdminEmail ||
            partner?.adminEmail ||
            partner?.contactEmail ||
            "",

          status: String(partner?.status || "")
            .trim()
            .toUpperCase(),

          invitation: partner?.invitation || "",

          protectProfileIdentity: Boolean(partner?.protectProfileIdentity),

          sharedData: Array.isArray(partner?.sharedData)
            ? partner.sharedData
            : [],
        }));

      console.log("Eligible profile-share partners:", normalizedPartners);

      setEligibleProfileSharePartners(normalizedPartners);
    } catch (error) {
      console.error("Unable to load eligible profile-sharing partners:", error);

      setEligibleProfileSharePartners([]);

      setProfileSharePartnersError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load partner organizations.",
      );
    } finally {
      setIsProfileSharePartnersLoading(false);
    }
  };

  const filteredProfileSharePartners = useMemo(() => {
    const searchValue = sharePartnerSearch.trim().toLowerCase();

    if (!searchValue) {
      return eligibleProfileSharePartners;
    }

    return eligibleProfileSharePartners.filter((partner) =>
      [partner.name, partner.orgCode, partner.admin, partner.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [eligibleProfileSharePartners, sharePartnerSearch]);

  const selectedProfileSharePartner =
    eligibleProfileSharePartners.find(
      (partner) => partner.id === sharePartnerSelection,
    ) || null;

  // useEffect(() => {
  //   if (!sharePartnerModalOpen) return;

  //   setSharePartnerSearch("");
  //   setShowSharePartnerResults(false);
  //   loadEligibleProfileSharePartners();
  // }, [sharePartnerModalOpen]);

  useEffect(() => {
    if (!sharePartnerModalOpen) {
      return;
    }

    setSharePartnerSearch("");
    setSharePartnerSelection("");
    setShowSharePartnerResults(true);

    loadEligibleProfileSharePartners();
  }, [sharePartnerModalOpen]);

  useEffect(() => {
    const handleSharePartnerOutsideClick = (event) => {
      if (
        sharePartnerPickerRef.current &&
        !sharePartnerPickerRef.current.contains(event.target)
      ) {
        setShowSharePartnerResults(false);
      }
    };

    document.addEventListener("mousedown", handleSharePartnerOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleSharePartnerOutsideClick);
    };
  }, []);
  const getExternalRoleMatches = (profile) => [
    { title: profile.role, department: profile.department, match: 86 },
    {
      title: "Business Intelligence Analyst",
      department: "Data Science",
      match: 81,
    },
    {
      title: "Documentation Specialist",
      department: "Knowledge Management",
      match: 76,
    },
  ];

  const openSaveViewModal = ({ internalIds = [], externalIds = [] }) => {
    const hasSelection = internalIds.length > 0 || externalIds.length > 0;

    if (!hasSelection) {
      MessageManager.showToast(
        "warning",
        "Please select at least one profile first.",
      );
      return;
    }

    setPendingSavedViewSelection({ internalIds, externalIds });
    setSaveViewName("");
    setSaveViewModalOpen(true);
  };

  const savePendingProfileView = () => {
    const trimmedName = saveViewName.trim();

    if (!trimmedName) return;

    setSavedProfileViews((prevViews) => [
      ...prevViews,
      {
        id: `sv-${Date.now()}`,
        name: trimmedName,
        internalIds: pendingSavedViewSelection.internalIds,
        externalIds: pendingSavedViewSelection.externalIds,
        updatedAt: "Just now",
      },
    ]);

    setSaveViewModalOpen(false);
    setSaveViewName("");
    setPendingSavedViewSelection({ internalIds: [], externalIds: [] });
    MessageManager.showToast("success", "Saved view created");
  };

  const getInternalProfileId = (profile) =>
    profile?._id || profile?.userId || profile?.employeeId || profile?.id;

  const getSavedViewInternalProfiles = (view) => {
    const ids = view?.internalIds || [];
    const apiProfiles = (employees || []).filter((employee) =>
      ids.includes(getInternalProfileId(employee)),
    );
    const apiIds = new Set(apiProfiles.map(getInternalProfileId));
    const dummyProfiles = savedViewDummyInternalProfiles.filter(
      (profile) => ids.includes(profile.id) && !apiIds.has(profile.id),
    );

    return [...apiProfiles, ...dummyProfiles];
  };

  const getSavedViewExternalProfiles = (view) =>
    (externalProfiles || []).filter((profile) =>
      (view?.externalIds || []).includes(profile.id),
    );

  const getSavedViewSearchText = (view) =>
    [view.name, ...(view.internalIds || []), ...(view.externalIds || [])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const filteredSavedProfileViews = savedProfileViews.filter((view) =>
    getSavedViewSearchText(view).includes(
      savedViewSearchTerm.trim().toLowerCase(),
    ),
  );

  const handleDeleteSavedView = (viewId) => {
    setSavedProfileViews((prevViews) =>
      prevViews.filter((view) => view.id !== viewId),
    );

    setSelectedSavedView((prevView) =>
      prevView?.id === viewId ? null : prevView,
    );

    MessageManager.showToast("success", "Saved view removed");
  };

  const handleRemoveProfileFromSavedView = ({
    viewId,
    profileType,
    profileId,
  }) => {
    const updateView = (view) => {
      if (view.id !== viewId) return view;

      return {
        ...view,
        internalIds:
          profileType === "internal"
            ? (view.internalIds || []).filter((id) => id !== profileId)
            : view.internalIds || [],
        externalIds:
          profileType === "external"
            ? (view.externalIds || []).filter((id) => id !== profileId)
            : view.externalIds || [],
        updatedAt: "Just now",
      };
    };

    setSavedProfileViews((prevViews) => prevViews.map(updateView));
    setSelectedSavedView((prevView) =>
      prevView?.id === viewId ? updateView(prevView) : prevView,
    );

    MessageManager.showToast("success", "Profile removed from saved view");
  };

  const renderSavedViewCount = (view) => (
    <div className="saved-view-counts" aria-label="Saved profile counts">
      <span>
        {view.internalIds.length} internal profile
        {view.internalIds.length === 1 ? "" : "s"}
      </span>
      <span className="saved-view-count-dot" aria-hidden="true" />
      <span>
        {view.externalIds.length} external profile
        {view.externalIds.length === 1 ? "" : "s"}
      </span>
    </div>
  );

  const renderProfileViewTabs = () => (
    <div
      className="profile-view-tabs"
      role="tablist"
      aria-label="Profile views"
    >
      <button
        type="button"
        className={`profile-view-tab ${profilesActiveView === "internal" ? "active" : ""}`}
        onClick={() => setProfilesActiveView("internal")}
      >
        Internal Profiles
      </button>
      {hasActivePartnerConnection && (
        <button
          type="button"
          className={`profile-view-tab ${profilesActiveView === "external" ? "active" : ""}`}
          onClick={() => setProfilesActiveView("external")}
        >
          External Profiles
        </button>
      )}
      <button
        type="button"
        className={`profile-view-tab ${profilesActiveView === "saved" ? "active" : ""}`}
        onClick={() => setProfilesActiveView("saved")}
      >
        Saved Profiles
      </button>
    </div>
  );

  const renderExternalAssessmentProgress = (profile) => {
    const assessment = profile.mayamayaAssessment || {};

    const questionsCompleted = Boolean(assessment.quizCompleted);
    const skillCompleted = Boolean(assessment.skillAssessmentCompleted);
    const resumeCompleted = Boolean(assessment.resumeUploadCompleted);

    return (
      <div
        className="assessment-progress-container external-assessment-progress-match"
        title={profile.profileStatus}
      >
        <div
          className={`assessment-step-item ${questionsCompleted ? "completed" : ""}`}
          title={
            questionsCompleted ? "Questions completed" : "Questions pending"
          }
        >
          <div
            className={`assessment-icon-circle ${questionsCompleted ? "completed" : ""}`}
          >
            <Lightbulb />
          </div>

          <span className="sprints-count">
            {questionsCompleted
              ? `Questions ${profile.completedQuestionsCount || 0}`
              : "Questions 0"}
          </span>
        </div>

        <div
          className={`progress-line ${questionsCompleted ? "completed" : ""}`}
        />

        <div
          className={`assessment-step-item ${skillCompleted ? "completed" : ""}`}
          title={
            skillCompleted ? "Skill Profile completed" : "Skill Profile pending"
          }
        >
          <div
            className={`assessment-icon-circle ${skillCompleted ? "completed" : ""}`}
          >
            <Award />
          </div>

          <span className="step-label">Skill Profile</span>
        </div>

        <div className={`progress-line ${skillCompleted ? "completed" : ""}`} />

        <div
          className={`assessment-step-item ${resumeCompleted ? "completed" : ""}`}
          title={resumeCompleted ? "Resume completed" : "Resume pending"}
        >
          <div
            className={`assessment-icon-circle ${resumeCompleted ? "completed" : ""}`}
          >
            <FileUp />
          </div>

          <span className="step-label">Resume</span>
        </div>
      </div>
    );
  };

  const filteredExternalProfiles = externalProfiles.filter((profile) => {
    const visibleName = profile.canViewCandidateDetails
      ? profile.candidateName
      : "";
    const matchSearch = [
      profile.id,
      visibleName,
      profile.partner,
      profile.availability,
      profile.profileStatus,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(externalSearchTerm.toLowerCase());

    const matchPartner =
      externalPartnerFilter === "All Partners" ||
      profile.partner === externalPartnerFilter;

    const matchAvailability =
      externalAvailabilityFilter === "All Availability" ||
      profile.availability === externalAvailabilityFilter;

    return matchSearch && matchPartner && matchAvailability;
  });
  const externalCurrentPageIds = filteredExternalProfiles.map(
    (profile) => profile.id,
  );

  const isAllExternalSelected =
    externalCurrentPageIds.length > 0 &&
    externalCurrentPageIds.every((id) => externalSelectedIds.includes(id));

  const toggleExternalOne = (id, checked) => {
    setExternalSelectedIds((prev) =>
      checked
        ? Array.from(new Set([...prev, id]))
        : prev.filter((item) => item !== id),
    );
  };

  const toggleExternalSelectAll = (checked) => {
    setExternalSelectedIds(checked ? externalCurrentPageIds : []);
  };

  const getExternalAppliedFiltersCount = () => {
    let count = 0;

    if (externalSearchTerm.trim()) count++;
    if (externalPartnerFilter !== "All Partners") count++;
    if (externalAvailabilityFilter !== "All Availability") count++;

    return count;
  };

  const handleExternalSearchButtonClick = () => {
    setExternalSearchTerm(externalSearchInputValue.trim());
  };

  const handleClearExternalSearch = () => {
    setExternalSearchInputValue("");
    setExternalSearchTerm("");
  };

  const clearAllExternalFilters = () => {
    setExternalSearchInputValue("");
    setExternalSearchTerm("");
    setExternalPartnerFilter("All Partners");
    setExternalAvailabilityFilter("All Availability");
    setShowExternalSearchBar(false);
    setShowExternalFiltersPanel(false);
  };

  const handleExternalBulkAction = (action, ids = []) => {
    if (!ids.length) {
      MessageManager.showToast(
        "warning",
        "Please select one or more external profiles first.",
      );
      return;
    }

    if (action === "save-view") {
      openSaveViewModal({ externalIds: ids });
      return;
    }

    if (action === "contact-partner") {
      MessageManager.showToast(
        "success",
        `Message prepared for ${ids.length} selected profile${ids.length === 1 ? "" : "s"}.`,
      );
      return;
    }

    if (action === "delete-external") {
      setExternalProfiles((prev) =>
        prev.filter((profile) => !ids.includes(profile.id)),
      );
      setExternalSelectedIds([]);
      MessageManager.showToast(
        "success",
        `${ids.length} external profile${ids.length === 1 ? "" : "s"} deleted.`,
      );
    }
  };

  const handleExternalCandidateViewToggle = (profile) => {
    navigator(`/external-candidates/${profile.id}`, {
      state: { profile, source: "external-profiles" },
    });
  };

  const closeExternalProfileModal = () => {
    setExternalProfileModalOpen(false);
    setTimeout(() => setSelectedExternalProfile(null), 150);
  };

  const handleExternalProfileContact = (profile) => {
    setSelectedExternalProfile(profile);
    setExternalActionMessage(
      `We are reviewing profile ${profile.id}. Please confirm current availability and next-step options.`,
    );
    setExternalActionModalOpen(true);
  };

  const handleExternalProfileContactSubmit = () => {
    setExternalActionModalOpen(false);
    MessageManager.showToast("success", "Partner contacted");
  };

  const handleDeleteExternalProfile = (profile) => {
    setExternalProfiles((previousProfiles) =>
      previousProfiles.filter((item) => item.id !== profile.id),
    );
    MessageManager.showToast("success", `Profile ${profile.id} removed.`);
  };

  // PAGINATION LOGIC
  const [peopleLength, setPeopleLength] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [orgTotalPeople, setOrgTotalPeople] = useState(null);

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const [inviteUserType, setInviteUserType] = useState("");
  const [invitePartner, setInvitePartner] = useState("");
  const [partnerInviteMessage, setPartnerInviteMessage] = useState("");
  const [partnerInvitePrepared, setPartnerInvitePrepared] = useState(false);

  // student fields
  const [inviteEducationLevel, setInviteEducationLevel] = useState("");
  const [inviteFieldOfStudy, setInviteFieldOfStudy] = useState("");
  const [inviteSection, setInviteSection] = useState("");

  const industryOptions = [
    "Education",
    "Healthcare",
    "IT",
    "Manufacturing",
    "Financial Services",
    "Other",
  ];
  const educationLevelOptions = [
    {
      value: "Middle-school",
      label: t("employees.invite.educationLevelOptions.middleSchool"),
    },
    {
      value: "High-school",
      label: t("employees.invite.educationLevelOptions.highSchool"),
    },
    {
      value: "College/University",
      label: t("employees.invite.educationLevelOptions.collegeUniversity"),
    },
  ];

  const fieldOfStudyOptions = [
    {
      value: "Arts, Design & Humanities",
      label: t("employees.invite.fieldOfStudyOptions.artsDesignHumanities"),
    },
    {
      value: "Business, Law & Finance",
      label: t("employees.invite.fieldOfStudyOptions.businessLawFinance"),
    },
    {
      value: "Health & Medicine",
      label: t("employees.invite.fieldOfStudyOptions.healthMedicine"),
    },
    {
      value: "Science & Technology",
      label: t("employees.invite.fieldOfStudyOptions.scienceTechnology"),
    },
  ];
  const isInviteFieldOfStudyRequired =
    inviteEducationLevel === "College/University";

  const readCareerProfileStatusFromUrl = () => {
    const raw = searchParams.get("careerProfileStatus");
    if (!raw) return null;

    const v = raw.trim().toLowerCase();
    if (v === "completed") return "Completed";
    if (v === "pending") return "Pending";
    if (v === "all") return "All";

    // fallback: allow custom values if ever needed
    return raw;
  };
  const readPerformanceMin = () => {
    const v = Number(searchParams.get("performanceMin"));
    return Number.isFinite(v) && v > 0 ? v : null;
  };

  const [performanceMin, setPerformanceMin] = useState(readPerformanceMin);

  // useEffect(() => {
  //   setPerformanceMin(readPerformanceMin());
  //   setCurrentPage(1);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [searchParams.toString()]);
  useEffect(() => {
    setPerformanceMin(readPerformanceMin());

    const cps = readCareerProfileStatusFromUrl();
    if (cps) setFilterCareerProfileStatus(cps);

    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };
  useEffect(() => {
    setPerformanceMin(readPerformanceMin());

    const sprintFromUrl = searchParams.get("sprintStatus");
    if (sprintFromUrl) {
      setFilterSprintsCompleted(readSprintStatusFromUrl());
    }

    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const [itemsPerPage, setItemsPerPage] = useState(20);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(peopleLength / itemsPerPage);

  const start = (peopleLength ?? 0) === 0 ? 0 : indexOfFirstItem + 1;
  const end =
    (peopleLength ?? 0) === 0
      ? 0
      : Math.min(indexOfFirstItem + employees.length, peopleLength);

  useEffect(() => {
    // Fetch employees from API
    getAllPeopleApiHandler();
  }, [
    currentPage,
    searchTerm,
    filterDepartment,
    filterLevel,
    filterAvailability,
    filterSprintsCompleted, // Add to dependency array
    filterCareerProfileStatus, // Add to dependency array
    filterResumeStatus, // Add to dependency array
    sortBy,
    sortOrder,
    itemsPerPage,
  ]);
  useEffect(() => {
    getDepartmentsListApiHandler();
  }, []);
  const [departmentsList, setDepartmentsList] = useState([]);
  const inviteDepartments = departmentsList.filter((d) => d._id !== "all");
  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();

      setDepartmentsList([
        {
          name: "All",
          _id: "all",
        },
        ...(response?.data || []),
      ]);
    } catch (error) {
      console.error("Error fetching departments list:", error);

      handleApiError(error, {
        showModal: false,
      });
    }
  };
  const [showInviteLink, setShowInviteLink] = useState("");
  const [showInviteLinkCopied, setShowInviteLinkCopied] = useState(false);
  const [isInviteLinkLoading, setIsInviteLinkLoading] = useState(false);
  const getpeopleInviteLinkHandler = async () => {
    if (inviteUserType === "External Candidate") {
      if (!invitePartner) {
        MessageManager.showToast(
          "warning",
          "Please select a partner before generating an external candidate invite link.",
        );
        return;
      }

      const safePartnerSlug = invitePartner
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      const inviteOrigin =
        typeof window !== "undefined" ? window.location.origin : "";

      const dummyLink = `${inviteOrigin}/invite/external-candidate?partner=${encodeURIComponent(
        safePartnerSlug,
      )}&type=external-candidate&token=demo-${Date.now()}`;

      setShowInviteLink(dummyLink);
      setShowInviteLinkCopied(true);
      setCopied(false);
      setPartnerInvitePrepared(false);
      setPartnerInviteMessage(
        `Hi ${invitePartner},\n\nPlease use the invite link below to invite external candidates for Hiring.\n\n${dummyLink}`,
      );
      MessageManager.showToast(
        "success",
        "External candidate invite link generated.",
      );
      return;
    }

    let obj = {
      department: invitePeopleDepartment,
      userRole: inviteUserType,
      educationLevel: inviteEducationLevel,
      fieldOfStudy: inviteFieldOfStudy,
      section: inviteSection,
    };
    try {
      setIsInviteLinkLoading(true);
      const response = await getPeopleInviteLink(obj);
      if (response.code === 200) {
        console.log("Invite link:", response.data);
        const link = response.data.inviteLink;
        setShowInviteLink(link);
        setShowInviteLinkCopied(true);
        if (inviteUserType === "External Candidate" && invitePartner) {
          setPartnerInviteMessage(
            `Hi ${invitePartner},\n\nPlease use the invite link below to share an external candidate profile for Hiring.\n\n${link}\n\nCandidate identity and contact details should remain protected. Please do not include candidate name, email, phone, resume, LinkedIn, portfolio, or personal contact details in any visible profile fields.`,
          );
          setPartnerInvitePrepared(false);
        }
      }
    } catch (error) {
      console.error("Error fetching invite link:", error);
      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setIsInviteLinkLoading(false);
    }
  };
  useEffect(() => {
    setSelectedIds([]);
  }, [
    searchTerm,
    filterDepartment,
    filterLevel,
    filterAvailability,
    filterSprintsCompleted,
    filterCareerProfileStatus,
    filterResumeStatus,
    sortBy,
    sortOrder,
  ]);

  // === Bulk select state
  const [selectedIds, setSelectedIds] = useState([]); // array of userIds

  const currentPageIds = (employees || []).map((p) => p._id);
  // const isAllSelected =
  //   currentPageIds.length > 0 &&
  //   currentPageIds.every((id) => selectedIds.includes(id));
  const isAllSelected =
    (peopleLength ?? 0) > 0 && selectedIds.length === (peopleLength ?? 0);

  // helper near your other hooks
  const deselect = (idsToClear) =>
    setSelectedIds((prev) => prev.filter((id) => !idsToClear.includes(id)));
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const bulkEditDepartmentApiHandler = async () => {
    if (!bulkEditDepartment || !selectedIds.length || isBulkSaving) return;
    try {
      setIsBulkSaving(true);
      const obj = { userIdArray: selectedIds, department: bulkEditDepartment };
      const response = await bulkEditPeopleDepartmentApi(obj);

      if (response.code === 200) {
        getAllPeopleApiHandler();
        deselect(selectedIds);
        setShowBulkDepartmentEdit(false);
        setBulkEditDepartment("");
        setShowModal({
          show: true,
          type: "success",
          title: "Bulk edit successful",
          message: response.message || t("employees.bulkEdit.successMessage"),
          showButton: false,
        });
      } else {
        setShowModal({
          show: true,
          type: "error",
          title: "Bulk edit failed",
          message: response.message || t("employees.bulkEdit.genericError"),
        });
      }
    } catch (error) {
      setShowModal({
        show: true,
        type: "error",
        title: "Bulk edit failed",
        message: error.message || t("employees.bulkEdit.errorMessage"),
      });
    } finally {
      setIsBulkSaving(false);
    }
  };

  const handleAction = async (action, ids = []) => {
    console.log("Bulk action:", action, ids);
    if (!ids?.length) {
      // Decide how to behave with 0 selection
      if (action === "download-excel") {
        // maybe export current filter set instead of selected rows
        await downloadPeopleDataHandler();
        return;
      }
      // For reminder actions, require selection
      setShowModal({
        show: true,
        type: "info",
        title: t("employees.messages.selectPeopleTitle"),
        message: t("employees.messages.selectPeopleMessage"),
      });
      return;
    }
    try {
      switch (action) {
        case "sprint-reminder": {
          await sendReminderEmailApi({
            userId: ids.map((id) => id),
            reminderType: "Sprint",
          });
          deselect(ids);
          setShowModal({
            show: true,
            type: "success",
            title: t("employees.messages.questionsRemindersSent"),
            message: t("employees.messages.remindersSentToPeople", {
              count: ids.length,
              personLabel:
                ids.length === 1 ? t("common.person") : t("common.people"),
            }),
          });
          break;
        }

        case "profile-reminder": {
          await sendReminderEmailApi({
            userId: ids.map((id) => id),
            reminderType: "Skill Profile",
          });
          deselect(ids);
          setShowModal({
            show: true,
            type: "success",
            title: t("employees.messages.profileRemindersSent"),
            message: t("employees.messages.remindersSentToPeople", {
              count: ids.length,
              personLabel:
                ids.length === 1 ? t("common.person") : t("common.people"),
            }),
          });
          break;
        }

        case "resume-reminder": {
          await sendReminderEmailApi({
            userId: ids.map((id) => id),
            reminderType: "Resume",
          });
          deselect(ids);
          setShowModal({
            show: true,
            type: "success",
            title: t("employees.messages.resumeRemindersSent"),
            message: t("employees.messages.remindersSentToPeople", {
              count: ids.length,
              personLabel:
                ids.length === 1 ? t("common.person") : t("common.people"),
            }),
          });
          break;
        }

        case "download-excel": {
          // If your API supports filtering by selected IDs:
          const obj = {
            userIdArray: ids,
            department: filterDepartment,
          };
          const res = await downloadPeopleDataApi(obj);
          // If the service returns a Blob/arraybuffer, download it:
          if (res?.data) {
            const blob = new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `employees_${new Date()
              .toISOString()
              .slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }
          deselect(ids);

          break;
        }
        case "department-edit": {
          // Handle department edit action
          setShowBulkDepartmentEdit(true);
          break;
        }
        case "share-with-partner": {
          if (!showSharedWithColumn) {
            MessageManager.showToast(
              "warning",
              "Enable profile sharing in Partner Settings first.",
            );
            break;
          }

          setSharePartnerProfileIds(ids);
          setSharePartnerSelection("");
          setSharePartnerModalOpen(true);
          break;
        }

        default:
          console.warn("Unknown action:", action);
      }
    } catch (error) {
      console.error("Bulk action failed:", error);
      handleApiError(error, { showModal: true });
    }
  };
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const fetchAllMatchingIds = async () => {
    // Try a fast path if your API supports returning all rows
    try {
      const fast = await getAllPeopleApi();
      // buildPeopleQuery({
      //   // many backends treat 0 as “no limit”
      //   fetchAll: true, // if your API supports this flag
      //   fields: ["_id"], // ask server for minimal fields if supported
      // })

      if (Array.isArray(fast?.data)) {
        const ids = fast.data.map((p) => p._id).filter(Boolean);
        const total = fast?.totalCount ?? ids.length;
        if (ids.length >= total) return ids; // got everything in one shot
      }
    } catch (_) {
      // ignore and fallback to pagination
    }

    // Fallback: paginate until we’ve collected all IDs
    const pageSizeForBulk = 1000; // tune per your API limits
    let pageNo = 1;
    let allIds = [];
    let total = null;

    // We must use the exact same filters
    while (true) {
      const resp = await getAllPeopleApi(
        buildPeopleQuery({
          pageNo,
          pageSize: pageSizeForBulk,
          fields: ["_id"],
        }),
      );
      const batch = (resp?.data ?? []).map((p) => p._id).filter(Boolean);
      allIds = allIds.concat(batch);

      total = total ?? resp?.totalCount ?? batch.length;
      if (batch.length === 0 || allIds.length >= total) break;

      pageNo += 1;
    }

    return Array.from(new Set(allIds)); // dedupe just in case
  };

  const buildPeopleQuery = (overrides = {}) => ({
    searchFilter: searchTerm,
    department: filterDepartment,
    level: filterLevel,
    availability: filterAvailability,
    sprintStatus: filterSprintsCompleted,
    careerProfileStatus: filterCareerProfileStatus,
    resumeStatus: filterResumeStatus,
    sortBy: sortBy,
    ...overrides,
  });

  const toggleSelectAll = async (checked) => {
    if (checked) {
      try {
        setSelectAllLoading(true);
        const ids = await fetchAllMatchingIds();
        console.log("Select-all fetched ids:", ids);
        setSelectedIds(ids); // no need to merge with current page; you now have every matching id
      } catch (err) {
        console.error("Select-all fetch failed:", err);
        handleApiError(err, { showModal: true });
      } finally {
        setSelectAllLoading(false);
      }
    } else {
      setSelectedIds([]); // clear all selections
    }
  };
  const shareWithPartnersHandler = async (partnerId, userIds) => {
    try {
      const obj = {
        partnerIds: [partnerId],
        userIdArray: userIds,
      };
      const res = await shareWithPartnersApi(obj);
      if (res.code === 200) {
        getAllPeopleApiHandler();
        MessageManager.showToast("success", "Changes saved");

        setSharePartnerModalOpen(false);
        setSharePartnerSearch("");
        setSharePartnerSelection("");
        setShowSharePartnerResults(false);
        setSharePartnerProfileIds([]);
        deselect(sharePartnerProfileIds);
      }
    } catch (error) {
      console.error("Error sharing profiles with partner:", error);
      handleApiError(error, { showModal: true });
    }
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) =>
      checked
        ? Array.from(new Set([...prev, id]))
        : prev.filter((x) => x !== id),
    );
  };
  const [copied, setCopied] = useState(false);

  // const handleCopyInviteLink = () => {
  //   const inviteLink = showInviteLink;
  //   window.navigator.clipboard.writeText(inviteLink);
  // };
  const handleCopyInviteLink = async () => {
    const inviteLink = showInviteLink || "";
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = inviteLink;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      MessageManager.showToast("success", "Invite link copied.");
      setTimeout(() => setCopied(false), 1800); // revert after 1.8s
    } catch (err) {
      console.error("Copy failed:", err);
      // optional: show a modal/toast here if you want
    }
  };

  const isUnfilteredQuery = () =>
    !searchTerm &&
    filterDepartment === "All" &&
    filterLevel === "All" &&
    filterAvailability === "All" &&
    filterSprintsCompleted === "All" &&
    filterCareerProfileStatus === "All" &&
    filterResumeStatus === "All" &&
    sortBy === "Name";

  const [isLoading, setIsLoading] = useState(false);
  const getAllPeopleApiHandler = async () => {
    try {
      setIsLoading(true);

      const obj = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        searchFilter: searchTerm,
        department: filterDepartment,
        level: filterLevel,
        availability: filterAvailability,
        sprintStatus: filterSprintsCompleted,
        careerProfileStatus: filterCareerProfileStatus,
        resumeStatus: filterResumeStatus,
        sortBy,
        performanceMin: performanceMin ?? undefined,
      };

      console.log("getAllPeopleApi payload:", obj);

      const response = await getAllPeopleApi(obj);

      const employeesWithAssessment = (response?.data || []).map((employee) => {
        const quizCompleted = isAssessmentComplete(
          employee.completedSprintCount,
        );

        const skillAssessmentCompleted = isAssessmentComplete(
          employee.careerProfileStatus,
        );

        const resumeUploadCompleted = isAssessmentComplete(
          employee.resumeStatus,
        );

        return {
          ...employee,
          mayamayaAssessment: {
            quizCompleted,
            skillAssessmentCompleted,
            resumeUploadCompleted,
            sprintsCompleted: employee.completedSprintCount,
          },
        };
      });

      setEmployees(employeesWithAssessment);
      setPeopleLength(response?.totalCount || null);

      setOrgTotalPeople((previousValue) => {
        if (typeof response?.orgTotalCount === "number") {
          return response.orgTotalCount;
        }

        if (isUnfilteredQuery() && typeof response?.totalCount === "number") {
          return response.totalCount;
        }

        return previousValue;
      });
    } catch (error) {
      console.error("Error fetching employees:", error);

      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEmployee = (employee) => {
    console.log("Removing employee from organization:", employee);
    setShowModal({
      show: true,
      type: "warning",
      title: t("employees.messages.removeFromOrganizationTitle"),
      message: t("employees.messages.removeFromOrganizationMessage", {
        name: employee.name,
      }),

      // team: teamData.teamName,
      showButtons: true,
      action: () => {
        removePeopleFromOrgHandler(employee);
      },
    });
  };

  //   const handleAddMember = (candidate) => {
  //   console.log("Adding member:", candidate);
  //   console.log("Adding team:", teamData);

  //   // await getTeamByIdApiHandler(paramId);

  //   setShowModal({
  //     show: true,
  //     type: "info",
  //     title: t("teamDetailPage.addTeamMember"),
  //     message: t("teamDetailPage.addMemberConfirm", {
  //       name: candidate.name,
  //       team: teamData.teamName,
  //     }),
  //     showLocalButton: true,
  //     action: () => {
  //       addMemberToTeamApiHandler(candidate);
  //     },
  //   });
  // };

  const removePeopleFromOrgHandler = async (employee) => {
    try {
      const obj = {
        // userIdArray: [employee.userId],
        userIdArray: [employee._id],
      };
      const res = await removePeopleFromOrgApi(obj);
      if (res.code === 200) {
        getAllPeopleApiHandler();
        setShowModal({
          show: true,
          type: "success",
          title: t("common.success"),
          message: t("employees.messages.removedFromOrganizationMessage", {
            name: employee.name,
          }),
        });
      }
    } catch (error) {
      console.error("Error removing employee from organization:", error);
      handleApiError(error, {
        showModal: true,
        title: t("common.error"),
        message:
          error.message ||
          t("employees.messages.failedToRemoveFromOrganization"),
      });
    }
  };

  // const CustomTooltip = ({ tooltip, children, id }) => {
  //   return (
  //     <div
  //       className="tooltip-container"
  //       onMouseEnter={() => setActiveTooltip(id)}
  //       onMouseLeave={() => setActiveTooltip(null)}
  //     >
  //       {children}
  //       {activeTooltip === id && (
  //         <div className="custom-tooltip">
  //           <div className="tooltip-header">
  //             <strong>{tooltip.title}</strong>
  //           </div>
  //           <div className="tooltip-message">{tooltip.message}</div>
  //           {tooltip.details && (
  //             <div className="tooltip-details">{tooltip.details}</div>
  //           )}
  //           {tooltip.action && (
  //             <div className="tooltip-action">💡 {tooltip.action}</div>
  //           )}
  //           <div className="tooltip-arrow"></div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);

    console.log("Fetching data for page:", currentPage);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const [viewMode, setViewMode] = useState("list"); // "card" or "list"
  const [show, setShow] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [ViewMoreModalTitle, setViewMoreModalTitle] = useState("");
  const handleViewMore = (
    skills,
    title = t("employees.viewMore.jobSkillsTitle"),
  ) => {
    setSelectedSkills(skills);
    console.log("skills", skills);
    setShow(true);
    setViewMoreModalTitle(title);
  };
  /*
   * The value is always sent to the API.
   * The label is translated and displayed in the UI.
   */

  const levels = [
    {
      value: "All",
      label: t("common.all"),
    },
    {
      value: "Junior",
      label: t("employees.levels.junior"),
    },
    {
      value: "Mid Level",
      label: t("employees.levels.midLevel"),
    },
    {
      value: "Senior",
      label: t("employees.levels.senior"),
    },
    {
      value: "Lead",
      label: t("employees.levels.lead"),
    },
  ];

  const availabilityOptions = [
    {
      value: "All",
      label: t("common.all"),
    },
    {
      value: "Available",
      label: t("employees.availabilityOptions.available"),
    },
    {
      value: "Not Available",
      label: t("employees.availabilityOptions.notAvailable"),
    },
  ];

  const statusOptions = [
    {
      value: "All",
      label: t("common.all"),
    },
    {
      value: "Completed",
      label: t("common.completed"),
    },
    {
      value: "Pending",
      label: t("common.pending"),
    },
  ];

  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortOptions = [
    { value: "Name", label: t("employees.listView.name") },
    { value: "Performance", label: t("employees.listView.performance") },
    { value: "Department", label: t("employees.listView.department") },
    {
      value: "Date Ascending",
      label: t("employees.listView.lastReportGeneratedAscending"),
      icon: <ArrowUp size={12} />,
    },
    {
      value: "Date Descending",
      label: t("employees.listView.lastReportGeneratedDescending"),
      icon: <ArrowDown size={12} />,
    },
  ];

  const selectedSortOption =
    sortOptions.find((option) => option.value === sortBy) || sortOptions[0];

  const handleSort = (data) => {
    return [...data].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (sortBy === "performance") {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (sortBy === "experience") {
        aValue = Number.parseInt(aValue);
        bValue = Number.parseInt(bValue);
      } else {
      }
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  function useMediaQuery(query) {
    const getMatch = () =>
      typeof window !== "undefined" && window.matchMedia(query).matches;

    const [matches, setMatches] = useState(getMatch);

    useEffect(() => {
      const mql = window.matchMedia(query);
      const onChange = (e) => setMatches(e.matches);

      // Support older Safari
      if (mql.addEventListener) mql.addEventListener("change", onChange);
      else mql.addListener(onChange);

      setMatches(mql.matches);

      return () => {
        if (mql.removeEventListener)
          mql.removeEventListener("change", onChange);
        else mql.removeListener(onChange);
      };
    }, [query]);

    return matches;
  }
  // Mobile = only card view (and hide toggle)
  const isMobile = useMediaQuery("(max-width: 767px)");
  const effectiveViewMode = isMobile ? "card" : viewMode;

  const filteredEmployees = employees.filter((employee) => {
    if (!showSharedWithColumn || filterSharedWith === "All") {
      return true;
    }

    const sharedPartners = getSharedPartners(employee);

    if (filterSharedWith === "Not shared") {
      return sharedPartners.length === 0;
    }

    return sharedPartners.includes(filterSharedWith);
  });

  // const handleTableSort = (field) => {
  //   if (tableSortBy === field) {
  //     setTableSortOrder((currentOrder) =>
  //       currentOrder === "asc" ? "desc" : "asc",
  //     );
  //     return;
  //   }

  //   setTableSortBy(field);
  //   setTableSortOrder("asc");
  // };

  // const getTableSortIcon = (field) => {
  //   if (tableSortBy !== field) {
  //     return <ChevronsUpDown size={14} className="table-sort-icon" />;
  //   }

  //   return tableSortOrder === "asc" ? (
  //     <ChevronUp size={14} className="table-sort-icon active" />
  //   ) : (
  //     <ChevronDown size={14} className="table-sort-icon active" />
  //   );
  // };

  // const getEmployeeTableSortValue = (employee, field) => {
  //   switch (field) {
  //     case "name":
  //       return employee.name || "";
  //     case "role":
  //       return employee.designation || "";
  //     case "lastReportGeneratedOn":
  //       return employee.lastReportGeneratedOn
  //         ? new Date(employee.lastReportGeneratedOn).getTime()
  //         : null;
  //     case "department":
  //       return employee.department || "";
  //     case "profileStatus":
  //       return [
  //         employee.mayamayaAssessment?.quizCompleted,
  //         employee.mayamayaAssessment?.skillAssessmentCompleted,
  //         employee.mayamayaAssessment?.resumeUploadCompleted,
  //       ].filter(Boolean).length;
  //     case "sharedWith":
  //       return getSharedWithText(employee) || "";
  //     default:
  //       return "";
  //   }
  // };

  // const compareEmployeeTableValues = (aValue, bValue) => {
  //   const aIsEmpty = aValue === null || aValue === undefined || aValue === "";
  //   const bIsEmpty = bValue === null || bValue === undefined || bValue === "";

  //   if (aIsEmpty && bIsEmpty) return 0;
  //   if (aIsEmpty) return 1;
  //   if (bIsEmpty) return -1;

  //   const comparison =
  //     typeof aValue === "number" && typeof bValue === "number"
  //       ? aValue - bValue
  //       : String(aValue).localeCompare(String(bValue), undefined, {
  //           numeric: true,
  //           sensitivity: "base",
  //         });

  //   return tableSortOrder === "asc" ? comparison : -comparison;
  // };

  // const sortedTableEmployees = [...filteredEmployees].sort((a, b) =>
  //   compareEmployeeTableValues(
  //     getEmployeeTableSortValue(a, tableSortBy),
  //     getEmployeeTableSortValue(b, tableSortBy),
  //   ),
  // );

  const handleTableSort = (field) => {
    if (tableSortBy === field) {
      setTableSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setTableSortBy(field);
    setTableSortOrder("asc");
  };

  const getTableSortIcon = (field) => {
    if (tableSortBy !== field) {
      return <ChevronsUpDown size={14} className="table-sort-icon" />;
    }

    return tableSortOrder === "asc" ? (
      <ChevronUp size={14} className="table-sort-icon active" />
    ) : (
      <ChevronDown size={14} className="table-sort-icon active" />
    );
  };

  const getEmployeeTableSortValue = (employee, field) => {
    switch (field) {
      case "name":
        return employee.name || "";
      case "role":
        return employee.designation || "";
      case "lastReportGeneratedOn":
        return employee.lastReportGeneratedOn
          ? new Date(employee.lastReportGeneratedOn).getTime()
          : null;
      case "department":
        return employee.department || "";
      case "profileStatus":
        return [
          employee.mayamayaAssessment?.quizCompleted,
          employee.mayamayaAssessment?.skillAssessmentCompleted,
          employee.mayamayaAssessment?.resumeUploadCompleted,
        ].filter(Boolean).length;
      /*case "sharedWith":
        return getSharedWithText(employee) || "";*/
      default:
        return "";
    }
  };

  const compareEmployeeTableValues = (aValue, bValue) => {
    const aIsEmpty = aValue === null || aValue === undefined || aValue === "";
    const bIsEmpty = bValue === null || bValue === undefined || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;

    const comparison =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: "base",
          });

    return tableSortOrder === "asc" ? comparison : -comparison;
  };

  const sortedTableEmployees = tableSortBy
    ? [...filteredEmployees].sort((a, b) =>
        compareEmployeeTableValues(
          getEmployeeTableSortValue(a, tableSortBy),
          getEmployeeTableSortValue(b, tableSortBy),
        ),
      )
    : filteredEmployees;

  const sortedEmployees = handleSort(filteredEmployees);

  const getPerformanceColor = (performance) => {
    if (performance >= 90) return "performance-excellent";
    if (performance >= 80) return "performance-good";
    return "performance-average";
  };

  const getSkillScoreColor = (skillScore) => {
    if (skillScore >= 90) return "skillScore-excellent";
    if (skillScore >= 80) return "skillScore-good";
    return "skillScore-average";
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case "Available":
        return "availability-available";
      case "Partially Available":
        return "availability-partial";
      default:
        return "availability-unavailable";
    }
  };

  const handleEmployeeClick = (employee) => {
    // navigator(`/employees/${employee.userId}`)
    navigator(`/employees/${employee._id}`);
  };

  const handleInviteEmployee = () => {
    setShowInvitePopup(true);
    // getpeopleInviteLinkHandler();
  };
  // component.jsx (handler stays simple)
  const downloadPeopleDataHandler = async () => {
    try {
      setIsLoading(true);

      const payload = {
        searchFilter: searchTerm,
        department: filterDepartment,
        level: filterLevel,
        availability: filterAvailability,
        sprintStatus: filterSprintsCompleted,
        careerProfileStatus: filterCareerProfileStatus,
        resumeStatus: filterResumeStatus,
        sortBy,
      };

      console.log("downloadPeopleDataApi payload:", payload);

      await downloadPeopleDataApi(payload);
    } catch (error) {
      console.error("Error downloading employee data:", error);

      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = () => {
    if (showModal.employeeId) {
      setEmployees((prev) =>
        prev.filter((emp) => emp.id !== showModal.employeeId),
      );
      setShowModal({
        show: false,
        type: "",
        title: "",
        message: "",
        employeeId: null,
      });
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleStatusEmail = async (statusType, idOrIds) => {
    // console.log("Sending status email for:", statusType, id);
    console.log(`Sending ${statusType} reminder email for IDs:`, idOrIds);
    try {
      const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
      const obj = {
        userId: ids,
        reminderType: statusType,
      };
      const response = await sendReminderEmailApi(obj);
      if (response.code === 200) {
        MessageManager.showToast(
          "success",
          t("employees.messages.reminderEmailSent"),
        );
      }
      console.log("Sending reminder email with data:", obj);
    } catch (error) {
      console.error("Error sending reminder email:", error);
      handleApiError(error, { showModal: true });
    }
  };

  const sanitizeSearch = (val) =>
    // allow letters, numbers, space, dash, underscore, and dot; strip everything else (incl. backslash)
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");

  // Redesigned collapsible filters for employees page
  // const [filtersCollapsed, setFiltersCollapsed] = useState(false)

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterDepartment !== "All") count++;
    if (filterLevel !== "All") count++;
    if (filterAvailability !== "All") count++;
    if (filterSprintsCompleted !== "All") count++;
    if (filterCareerProfileStatus !== "All") count++;
    if (filterResumeStatus !== "All") count++;
    if (showSharedWithColumn && filterSharedWith !== "All") count++;
    if (sortBy !== "Name") count++;
    return count;
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterDepartment("All");
    setFilterLevel("All");
    setFilterAvailability("All");
    setFilterSprintsCompleted("All");
    setFilterCareerProfileStatus("All");
    setFilterResumeStatus("All");
    setFilterSharedWith("All");
    setSortBy("Name");
    setSortOrder("asc");
    setTableSortBy(null);
    setTableSortOrder("asc");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (location.state?.openInvitePopup) {
      setShowInvitePopup(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const renderProfilesListHeader = ({ loading = false } = {}) => (
    <div
      className={`list-header ${
        showSharedWithColumn ? "with-shared-with" : "without-shared-with"
      }`}
    >
      <div className="list-col list-select-col">
        <input
          type="checkbox"
          checked={!loading && isAllSelected}
          disabled={loading || selectAllLoading || (peopleLength ?? 0) === 0}
          onChange={(event) => toggleSelectAll(event.target.checked)}
          aria-label="Select all profiles"
          title="Select all profiles"
        />
      </div>

      <div
        className="list-col table-sortable"
        onClick={() => handleTableSort("name")}
      >
        {t("employees.listView.name")}
        {getTableSortIcon("name")}
      </div>

      <div
        className="list-col table-sortable"
        onClick={() => handleTableSort("role")}
      >
        {t("employees.listView.role")}
        {getTableSortIcon("role")}
      </div>

      <div
        className="list-col table-sortable"
        onClick={() => handleTableSort("lastReportGeneratedOn")}
      >
        {t("employees.listView.lastReportGenerated")}
        {getTableSortIcon("lastReportGeneratedOn")}
      </div>

      <div
        className="list-col table-sortable"
        onClick={() => handleTableSort("department")}
      >
        {t("employees.listView.department")}
        {getTableSortIcon("department")}
      </div>

      <div
        className="list-col table-sortable"
        onClick={() => handleTableSort("profileStatus")}
      >
        {t("employees.listView.profileStatus")}
        {getTableSortIcon("profileStatus")}
      </div>

      {showSharedWithColumn && (
        <div
          className="list-col shared-with-header table-sortable"
          title="Shows which partner organizations can access this profile through profile sharing settings"
          onClick={() => handleTableSort("sharedWith")}
        >
          Shared With
          <Info size={14} />
          {getTableSortIcon("sharedWith")}
        </div>
      )}

      <div className="list-col">{t("employees.listView.actions")}</div>
    </div>
  );
  return (
    <div className="employees-page">
      <BackButton />
      <main className="employees-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <User size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("employees.pageTitle")}</h1>
              <CustomTooltip
                tooltip={{
                  title: t("employees.tooltips.readiness.title"),
                  message: t("employees.tooltips.readiness.message"),
                }}
                id="mobility-recommendation-info"
              >
                <Info
                  size={18}
                  style={{
                    color: "var(--text-secondary)",
                    cursor: "pointer",
                  }}
                />
              </CustomTooltip>
            </div>
            <p>{t("employees.pageDescription")}</p>
          </div>

          {/* Updated Filter Panel */}
          <div className="smart-filter-panel">
            <div className="smart-filter-top-bar">
              {/* left side (badge/info) */}
              <div className="smart-filter-info">
                {getAppliedFiltersCount() > 0 && (
                  <>
                    <span className="smart-filter-badge">
                      {getAppliedFiltersCount()}
                    </span>
                    <span>{t("common.filtersApplied")}</span>
                  </>
                )}
              </div>

              {/* RIGHT side actions (search input + icons) */}
              <div className="smart-filter-actions-right">
                {/* This slot always exists. It expands/collapses but icons never move */}
                <div
                  className={`smart-search-slot ${showSearchBar ? "open" : ""}`}
                >
                  <input
                    type="text"
                    placeholder={t("employees.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) =>
                      setSearchInputValue(sanitizeSearch(e.target.value))
                    }
                    className="smart-search-inline-input"
                    autoFocus={showSearchBar}
                  />
                  <span className="mobile-search-placeholder-hint">
                    {t("employees.searchPlaceholder")}
                  </span>

                  <span className="mobile-search-placeholder-hint">
                    {t("employees.searchPlaceholder")}
                  </span>

                  <span className="mobile-search-placeholder-hint">
                    {t("employees.searchPlaceholder")}
                  </span>

                  {searchTerm && (
                    <button
                      className="smart-search-clear"
                      onClick={handleClearSearch}
                      title={t("common.clear")}
                      type="button"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>

                {/* Search icon always stays in same place (right side) */}
                <button
                  className={`smart-filter-icon-btn ${showSearchBar ? "active" : ""}`}
                  title={
                    showSearchBar ? t("common.closeSearch") : t("common.search")
                  }
                  onClick={() => {
                    if (!showSearchBar) {
                      setSearchInputValue(searchTerm);
                      setShowSearchBar(true);
                      setShowFiltersPanel(false);
                      return;
                    }

                    handleSearchButtonClick();
                  }}
                  type="button"
                >
                  <Search size={18} />
                </button>

                <div className="smart-filter-separator" />

                <button
                  className={`smart-filter-icon-btn ${showFiltersPanel ? "active" : ""}`}
                  onClick={() => {
                    setShowFiltersPanel((v) => !v);
                    setShowSearchBar(false);
                  }}
                  title="Filters"
                  type="button"
                >
                  <Filter size={18} />
                </button>
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("employees.filters.department")}</label>
                      <select
                        value={filterDepartment}
                        onChange={(event) => {
                          setFilterDepartment(event.target.value);
                          setCurrentPage(1);
                        }}
                        className={`smart-filter-select ${
                          filterDepartment !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departmentsList.map((department) => (
                          <option key={department._id} value={department.name}>
                            {department._id === "all"
                              ? t("common.all")
                              : department.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="smart-filter-item">
                      <label>{t("employees.filters.level")}</label>
                      <select
                        value={filterLevel}
                        onChange={(event) => {
                          setFilterLevel(event.target.value);
                          setCurrentPage(1);
                        }}
                        className={`smart-filter-select ${
                          filterLevel !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {levels.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="smart-filter-item">
                      <label>{t("employees.filters.availability")}</label>
                      <select
                        value={filterAvailability}
                        onChange={(event) => {
                          setFilterAvailability(event.target.value);
                          setCurrentPage(1);
                        }}
                        className={`smart-filter-select ${
                          filterAvailability !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {availabilityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="smart-filter-item">
                      <label>{t("employees.filters.sprintsCompleted")}:</label>
                      <select
                        value={filterSprintsCompleted}
                        onChange={(event) => {
                          setFilterSprintsCompleted(event.target.value);
                          setCurrentPage(1);
                        }}
                        className={`smart-filter-select ${
                          filterSprintsCompleted !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {statusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="smart-filter-item">
                      <label>
                        {t("employees.filters.careerProfileStatus")}:
                      </label>
                      <select
                        value={filterCareerProfileStatus}
                        onChange={(event) => {
                          setFilterCareerProfileStatus(event.target.value);
                          setCurrentPage(1);
                        }}
                        className={`smart-filter-select ${
                          filterCareerProfileStatus !== "All"
                            ? "smart-active"
                            : ""
                        }`}
                      >
                        {statusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="smart-filter-item">
                      <label>{t("employees.filters.resumeStatus")}:</label>
                      <select
                        value={filterResumeStatus}
                        onChange={(event) => {
                          setFilterResumeStatus(event.target.value);
                          setCurrentPage(1);
                        }}
                        className={`smart-filter-select ${
                          filterResumeStatus !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {statusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {showSharedWithColumn && (
                      <div className="smart-filter-item">
                        <label>Shared with</label>

                        <select
                          value={filterSharedWith}
                          onChange={(event) => {
                            setFilterSharedWith(event.target.value);
                            setCurrentPage(1);
                          }}
                          className={`smart-filter-select ${
                            filterSharedWith !== "All" ? "smart-active" : ""
                          }`}
                        >
                          <option value="All">All profiles</option>
                          <option value="Not shared">Not shared</option>

                          {sharedWithPartnerOptions.map((partner) => (
                            <option key={partner} value={partner}>
                              {partner}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}{" "}
                    <div className="smart-filter-item">
                      <label>{t("employees.filters.sortBy")}</label>

                      <div
                        className={`smart-sort-dropdown ${
                          sortBy !== "Name" ? "smart-active" : ""
                        }`}
                        tabIndex={0}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setShowSortDropdown(false);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="smart-sort-trigger"
                          onClick={() => setShowSortDropdown((prev) => !prev)}
                        >
                          <span className="smart-sort-value">
                            {selectedSortOption.icon && (
                              <span className="smart-sort-icon">
                                {selectedSortOption.icon}
                              </span>
                            )}
                            {selectedSortOption.label}
                          </span>

                          <ChevronDown
                            size={14}
                            className={`smart-sort-chevron ${
                              showSortDropdown ? "open" : ""
                            }`}
                          />
                        </button>

                        {showSortDropdown && (
                          <div className="smart-sort-menu">
                            {sortOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`smart-sort-option ${
                                  sortBy === option.value ? "selected" : ""
                                }`}
                                onClick={() => {
                                  setSortBy(option.value);
                                  setTableSortBy(null);
                                  setTableSortOrder("asc");
                                  setCurrentPage(1);
                                  setShowSortDropdown(false);
                                }}
                              >
                                <span>{option.label}</span>

                                {option.icon && (
                                  <span className="smart-sort-icon">
                                    {option.icon}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* The rest of the original code for page-actions, bulk-actions, rendering, and modals */}
          <div className="page-actions">
            {!isMobile && (
              <div
                className="view-toggle"
                style={{ position: "absolute", top: "105px", right: "260px" }}
              >
                <button
                  className={`view-btn ${viewMode === "card" ? "active" : ""}`}
                  onClick={() => setViewMode("card")}
                  title={t("employees.cardViewBtn")}
                >
                  <Grid size={16} />
                </button>

                <button
                  className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  title={t("employees.listViewBtn")}
                >
                  <List size={16} />
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: "12px" }}>
              {(orgTotalPeople ?? peopleLength ?? 0) > 0 && (
                <button
                  disabled={!peopleAccess.canInvitePeople}
                  className="btn btn-primary create-team-btn"
                  onClick={handleInviteEmployee}
                  title={
                    showDisabledInviteTooltip ? permissionDeniedMessage : ""
                  }
                >
                  <Plus size={16} />
                  {t("employees.inviteEmployee")}
                </button>
              )}
            </div>
          </div>

          {/* TOP FILTERS (Original Location - now replaced by tm-filters-container) */}
          {/* <div className="usearch-filters" style={{ marginTop: "-70px" }}>
            <div className="usearch-filters-row">
              <div className="usearch-container">
                <Search size={20} className="usearch-icon" />
                <input
                  type="text"
                  placeholder={t("employees.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(sanitizeSearch(e.target.value))
                  }
                  className="search-input"
                />
                {/*<small className="usearch-note">
                  {t?.("upskillPage.searchRestrictions") ||
                    "Note: Special characters are restricted (e.g., \\)."}
                </small>* /}
              </div>

              <div className="ufilter-group">
                <label>
                  <Filter size={14} />
                  {t("employees.filters.department")}
                </label>
                <select
                  value={filterDepartment}
                  // onChange={(e) => setFilterDepartment(e.target.value)}
                  onChange={(e) => {
                    if (e.target.value === "__add__") {
                      navigator("/settings/organization-info");
                      return;
                    }
                    setFilterDepartment(e.target.value);
                  }}
                  className="ufilter-select"
                >
                  {departmentsList.length === 0 ? (
                    <>
                      <option value="">No departments available</option>
                      <option value="__add__">Create Department</option>
                    </>
                  ) : (
                    <>
                      <option value="">Select department</option>
                      {departmentsList.map((dept) => (
                        <option key={dept._id} value={dept.name}>
                          {dept.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div className="ufilter-group">
                <label>
                  <Filter size={14} />
                  {t("employees.filters.level")}
                </label>
                <select
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e.target.value)}
                  className="ufilter-select"
                >
                  {levels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ufilter-group">
                <label>
                  <Filter size={14} />
                  {t("employees.filters.availability")}
                </label>
                <select
                  value={filterAvailability}
                  onChange={(e) => setFilterAvailability(e.target.value)}
                  className="ufilter-select"
                >
                  {availabilityOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="usearch-filters-row" style={{ marginTop: "25px" }}>
              {/* New Filter: Sprints Completed * /}
              <div className="ufilter-group">
                <label>
                  <Filter size={14} />
                  {t("employees.filters.sprintsCompleted")}
                </label>
                <select
                  value={filterSprintsCompleted}
                  onChange={(e) => setFilterSprintsCompleted(e.target.value)}
                  className="ufilter-select"
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* New Filter: Career Profile Status * /}
              <div className="ufilter-group">
                <label>
                  <Filter size={14} />
                  {t("employees.filters.careerProfileStatus")}
                </label>
                <select
                  value={filterCareerProfileStatus}
                  onChange={(e) => setFilterCareerProfileStatus(e.target.value)}
                  className="ufilter-select"
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* New Filter: Resume Status * /}
              <div className="ufilter-group">
                <label>
                  <Filter size={14} />
                  {t("employees.filters.resumeStatus")}
                </label>
                <select
                  value={filterResumeStatus}
                  onChange={(e) => setFilterResumeStatus(e.target.value)}
                  className="ufilter-select"
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ufilter-group">
                <label>
                  <SortAsc size={14} />
                  {t("employees.filters.sortBy")}
                </label>
                <div className="sort-controls">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="ufilter-select"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* <div className="ufilter-group">
                <button
                  className="excel-download-btn btn btn-primary "
                  onClick={downloadPeopleDataHandler}
                >
                  <Download size={16} /> Excel Download
                </button>
              </div> * /}
            </div>
          </div> */}

          {/* BULK ACTIONS */}
          <div className="u-page-actions">
            <label
              className="select-all-wrap"
              style={{ fontSize: 16, display: "flex", alignItems: "center" }}
            >
              <input
                type="checkbox"
                style={{ width: 15, height: 15, transform: "scale(1.1)" }}
                checked={isAllSelected}
                disabled={selectAllLoading || (peopleLength ?? 0) === 0}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
              <span>
                {selectAllLoading
                  ? t("common.selecting")
                  : t("common.selectAll")}
                {!!peopleLength &&
                  !selectAllLoading &&
                  isAllSelected &&
                  ` (${peopleLength})`}
              </span>
            </label>

            <BulkAction
              options={[
                {
                  value: "sprint-reminder",
                  label: t("employees.bulkActions.questionsReminder"),
                },
                {
                  value: "profile-reminder",
                  label: t("employees.bulkActions.profileReminder"),
                },
                {
                  value: "resume-reminder",
                  label: t("employees.bulkActions.resumeReminder"),
                },
                {
                  value: "download-excel",
                  label: t("employees.bulkActions.downloadData"),
                },
                {
                  value: "department-edit",
                  label: t("employees.bulkActions.editDepartment"),
                  disabled: !peopleAccess.canEditPeople,
                },
                ...(showSharedWithColumn
                  ? [
                      {
                        value: "share-with-partner",
                        label: "Share with Partner",
                      },
                    ]
                  : []),
              ]}
              label={t("employees.bulkActions.bulkAction")}
              placeholder={t("employees.bulkActions.selectBulkAction")}
              selectedIds={selectedIds}
              onAction={handleAction}
              requireSelection={true}
            />
          </div>

          {isLoading ? (
            effectiveViewMode === "card" ? (
              <div className="employees-grid">
                {Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : (
              <div className="employees-list">
                {renderProfilesListHeader({ loading: true })}
                {Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonListRow key={index} />
                ))}
              </div>
            )
          ) : filteredEmployees.length === 0 ? (
            getAppliedFiltersCount() > 0 ? (
              <FilterNoResults
                title={t("common.noResults")}
                message={t("common.adjustOrClear")}
                clearLabel={t("common.clearFilters")}
                onClear={clearAllFilters}
              />
            ) : // Empty state for both card and list views
            effectiveViewMode === "card" ? (
              // <div className="employees-grid">
              //   <div className="employee-card" style={{ textAlign: "center", padding: "40px" }}>
              //     <User size={48} style={{ color: "#ccc", marginBottom: "16px" }} />
              //     <h3 style={{ color: "#666", marginBottom: "8px" }}>No Employees Found</h3>
              //     <p style={{ color: "#999" }}>Try adjusting your search criteria or invite new employees.</p>
              //   </div>
              // </div>
              <EmptyState
                icon={User}
                iconSize={48}
                title={t("employees.emptyTitle")}
                message={t("employees.emptyMessage")}
                actionLabel={t("employees.inviteEmployee")}
                onAction={handleInviteEmployee}
              />
            ) : (
              // <div className="employees-list">
              //   <div className="list-header">
              //     <div className="list-col">{t("employees.listView.name")}</div>
              //     <div className="list-col">{t("employees.listView.role")}</div>
              //     <div className="list-col">
              //       {t("employees.listView.department")}
              //     </div>
              //     <div className="list-col">
              //       {t("employees.listView.performance")}
              //     </div>
              //     <div className="list-col">
              //       {t("employees.listView.profileStatus")}
              //     </div>
              //     <div className="list-col">
              //       {t("employees.listView.actions")}
              //     </div>
              //   </div>
              //    <div
              //     className="list-row"
              //     style={{
              //       display: "flex", // Make it a flex container
              //       justifyContent: "center", // Center content horizontally
              //       alignItems: "center", // Optional: center vertically if needed
              //       padding: "40px",
              //       gridColumn: "1 / -1",
              //     }}
              //   >
              //     <div
              //       style={{
              //         display: "flex",
              //         flexDirection: "column",
              //         alignItems: "center", // Center items inside column
              //         textAlign: "center", // Center text
              //         width: "100%",
              //         maxWidth: "400px", // Optional: limits content width
              //       }}
              //     >
              //       <User
              //         size={48}
              //         style={{ color: "#ccc", marginBottom: "16px" }}
              //       />
              //       <h3 style={{ color: "#666", marginBottom: "8px" }}>
              //         No Employees Found
              //       </h3>
              //       <p style={{ color: "#999" }}>
              //         Try adjusting your search criteria or invite new
              //         employees.
              //       </p>
              //     </div>
              //   </div>
              // </div>
              <EmptyState
                icon={User}
                iconSize={48}
                title={t("employees.emptyTitle")}
                message={t("employees.emptyMessage")}
                actionLabel={t("employees.inviteEmployee")}
                onAction={handleInviteEmployee}
              />
            )
          ) : effectiveViewMode === "card" ? (
            <div className="employees-grid">
              {filteredEmployees.map((employee) => (
                <div key={employee.id} className="employee-card">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(employee._id)}
                    onChange={(e) => toggleOne(employee._id, e.target.checked)}
                  />
                  <div className="card-header">
                    <div className="employee-info">
                      <div className="employee-avatar">
                        <User
                          size={20}
                          onClick={() => handleEmployeeClick(employee)}
                        />
                      </div>
                      <div>
                        <h3 onClick={() => handleEmployeeClick(employee)}>
                          {employee.name}
                        </h3>
                        <p className="employee-profile-id">
                          Profile ID: {getProfileId(employee)}
                        </p>
                        <p className="employee-role">{employee.designation}</p>
                        <p className="employee-department">
                          {employee.department}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="employee-details"
                    // onClick={() => handleEmployeeClick(employee)}
                    style={{
                      textAlign: "left",
                      width: "100%",
                      justifyContent: "left",
                    }}
                  >
                    <div className="detail-row" style={{ width: "100%" }}>
                      <Briefcase size={14} />
                      <span>
                        {employee.experience
                          ? employee.experience
                          : t("common.notAvailable")}{" "}
                        {t("employees.results.experience")}
                      </span>
                    </div>
                    <div className="detail-row" style={{ width: "100%" }}>
                      <TrendingUp size={14} />
                      <span>
                        {t("employees.filters.level")}{" "}
                        {employee.level
                          ? employee.level
                          : t("common.notAvailable")}
                      </span>
                    </div>
                    <div className="detail-row" style={{ width: "100%" }}>
                      <Calendar size={14} />
                      <span>
                        {t("employees.listView.lastReportGenerated")}
                        {":"}{" "}
                        {employee.lastReportGeneratedOn
                          ? formatEmployeeCreatedAt(
                              employee.lastReportGeneratedOn,
                            )
                          : t("common.notAvailable")}
                      </span>
                    </div>

                    {/*<div className="detail-row" style={{ width: "100%" }}>
                      <TrendingUp size={14} />
                      <span>Performance: {employee.skillScore}%</span>
                    </div>*/}
                    <div className="detail-row" style={{ width: "100%" }}>
                      {/* <span
                        className={`availability-badge ${getAvailabilityColor(
                          employee.availability
                        )}`}
                      >
                        {employee.availability}
                      </span> */}

                      {/* <span
                        className={`availability-badge ${getSkillScoreColor(
                          employee.skillScore
                        )}`}
                      >
                        {employee.skillScore}
                      </span> */}
                    </div>
                  </div>

                  <div
                    className="em-skills-section"
                    // onClick={() => handleEmployeeClick(employee)}
                  >
                    <h4 style={{ margin: "0", marginBottom: "12px" }}>
                      {t("employees.results.keySkills")}
                    </h4>
                    <div className="em-skills-tags">
                      {employee?.extractedHardSkills
                        .slice(0, 3)
                        .map((skill, index) => (
                          <span key={index} className="em-skill-tag">
                            {skill}
                          </span>
                        ))}
                      {employee?.extractedHardSkills.length > 3 && (
                        <span
                          className="em-skill-tag more"
                          onClick={() => {
                            handleViewMore(
                              employee.extractedHardSkills,
                              t("employees.viewMore.keySkillsTitle"),
                            );
                          }}
                        >
                          +{employee.extractedHardSkills.length - 3}{" "}
                          {t("employees.results.more")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* New Assessment Progress Section for Card View */}
                  {employee.mayamayaAssessment && (
                    <div className="assessment-section mt-4">
                      <h4
                        style={{
                          marginTop: "20px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {t("employees.assessment.title")}
                      </h4>
                      <div className="assessment-progress-container">
                        {/* Quiz Step */}
                        <div
                          onClick={() =>
                            !employee.mayamayaAssessment.quizCompleted
                              ? handleStatusEmail("Sprint", employee._id)
                              : null
                          }
                          className={`assessment-step-item ${
                            employee.mayamayaAssessment.quizCompleted
                              ? "completed"
                              : ""
                          }`}
                          title={
                            employee.mayamayaAssessment.quizCompleted
                              ? t("employees.quizCompleted")
                              : t("employees.quizPending")
                          }
                        >
                          <div
                            className={`assessment-icon-circle ${
                              employee.mayamayaAssessment.quizCompleted
                                ? "completed"
                                : ""
                            }`}
                          >
                            <Lightbulb />
                          </div>
                          <span className="step-label">
                            {t("employees.assessment.quiz")}
                          </span>

                          {employee.mayamayaAssessment.quizCompleted ? (
                            <span className="sprints-count">
                              {t("employees.assessment.questions", {
                                count: employee.completedQuestionsCount,
                              })}
                            </span>
                          ) : (
                            <span className="sprints-count">Questions 0</span>
                          )}
                        </div>

                        {/* Line between Quiz and Skill */}
                        <div
                          className={`progress-line ${
                            employee.mayamayaAssessment.quizCompleted
                              ? "completed"
                              : ""
                          }`}
                        ></div>

                        {/* Skill Assessment Step */}
                        <div
                          onClick={() =>
                            !employee.mayamayaAssessment
                              .skillAssessmentCompleted
                              ? handleStatusEmail("Skill Profile", employee._id)
                              : null
                          }
                          className={`assessment-step-item ${
                            employee.mayamayaAssessment.skillAssessmentCompleted
                              ? "completed"
                              : ""
                          }`}
                          title={
                            employee.mayamayaAssessment.skillAssessmentCompleted
                              ? t("employees.skillCompleted")
                              : t("employees.skillPending")
                          }
                        >
                          <div
                            className={`assessment-icon-circle ${
                              employee.mayamayaAssessment
                                .skillAssessmentCompleted
                                ? "completed"
                                : ""
                            }`}
                          >
                            <Award />
                          </div>
                          <span className="step-label">
                            {t("employees.assessment.skillProfile")}
                          </span>
                        </div>

                        {/* Line between Skill and Resume */}
                        <div
                          className={`progress-line ${
                            employee.mayamayaAssessment.skillAssessmentCompleted
                              ? "completed"
                              : ""
                          }`}
                        ></div>

                        {/* Resume Upload Step */}
                        <div
                          onClick={() =>
                            !employee.mayamayaAssessment.resumeUploadCompleted
                              ? handleStatusEmail("Resume", employee._id)
                              : null
                          }
                          className={`assessment-step-item ${
                            employee.mayamayaAssessment.resumeUploadCompleted
                              ? "completed"
                              : ""
                          }`}
                          title={
                            employee.mayamayaAssessment.resumeUploadCompleted
                              ? t("employees.resumeCompleted")
                              : t("employees.resumePending")
                          }
                        >
                          <div
                            className={`assessment-icon-circle ${
                              employee.mayamayaAssessment.resumeUploadCompleted
                                ? "completed"
                                : ""
                            }`}
                          >
                            <FileUp />
                          </div>
                          <span className="step-label">
                            {t("employees.assessment.resume")}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="employees-list">
              {renderProfilesListHeader()}

              {sortedTableEmployees.map((employee) => (
                <div
                  key={employee.id}
                  className={`list-row ${showSharedWithColumn ? "with-shared-with" : "without-shared-with"}`}
                >
                  <div
                    className="list-col list-select-col"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(employee._id)}
                      onChange={(event) =>
                        toggleOne(employee._id, event.target.checked)
                      }
                      aria-label={`Select ${employee.name || "profile"}`}
                    />
                  </div>
                  <div
                    className="list-col"
                    onClick={() => handleEmployeeClick(employee)}
                  >
                    <div className="employee-name-cell">
                      <div className="employee-avatar-small">
                        <User size={16} />
                      </div>
                      <div className="employee-name-stack">
                        <span
                          className="employee-name-text"
                          title={employee.name || undefined}
                        >
                          {employee.name ? employee.name : "-"}
                        </span>
                        <span className="employee-profile-id">
                          Profile ID: {getProfileId(employee)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="list-col">
                    {employee.designation ? employee.designation : "-"}
                  </div>
                  <div className="list-col">
                    {employee.lastReportGeneratedOn
                      ? formatEmployeeCreatedAt(employee.lastReportGeneratedOn)
                      : "-"}
                  </div>

                  <div className="list-col">
                    {employee.department ? employee.department : "-"}
                  </div>
                  {/*<div className="list-col">
                    <span className={`availability-badge ${getSkillScoreColor(employee.skillScore)}`}>
                      {employee.skillScore}%
                    </span>
                  </div>*/}
                  {/* Assessment Progress Column */}
                  <div className="list-col assessment-progress-cell">
                    {employee.mayamayaAssessment && (
                      <div className="assessment-progress-container">
                        {/* Quiz Step */}
                        <div
                          onClick={() =>
                            !employee.mayamayaAssessment.quizCompleted
                              ? handleStatusEmail("Sprint", employee._id)
                              : null
                          }
                          className={`assessment-step-item ${
                            employee.mayamayaAssessment.quizCompleted
                              ? "completed"
                              : ""
                          }`}
                          title={
                            employee.mayamayaAssessment.quizCompleted
                              ? t("employees.quizCompleted")
                              : t("employees.quizPending")
                          }
                        >
                          <div
                            className={`assessment-icon-circle ${
                              employee.mayamayaAssessment.quizCompleted
                                ? "completed"
                                : ""
                            }`}
                          >
                            <Lightbulb />
                          </div>
                          {employee.mayamayaAssessment.quizCompleted ? (
                            <span className="sprints-count">
                              {/* {employee.mayamayaAssessment
                                .sprintsCompleted === 0
                                ? "Pending"
                                : `Reports: ${employee.mayamayaAssessment.sprintsCompleted}`} */}
                              {t("employees.assessment.questions", {
                                count: employee.completedQuestionsCount,
                              })}
                            </span>
                          ) : (
                            <span style={{ fontSize: "10px" }}>
                              {t("employees.assessment.questionsZero")}
                            </span>
                          )}
                        </div>

                        {/* Line between Quiz and Skill */}
                        <div
                          className={`progress-line ${
                            employee.mayamayaAssessment.quizCompleted
                              ? "completed"
                              : ""
                          }`}
                        ></div>

                        {/* Skill Assessment Step */}
                        <div
                          onClick={() =>
                            !employee.mayamayaAssessment
                              .skillAssessmentCompleted
                              ? handleStatusEmail("Skill Profile", employee._id)
                              : null
                          }
                          className={`assessment-step-item ${
                            employee.mayamayaAssessment.skillAssessmentCompleted
                              ? "completed"
                              : ""
                          }`}
                          title={
                            employee.mayamayaAssessment.skillAssessmentCompleted
                              ? t("employees.skillCompleted")
                              : t("employees.skillPending")
                          }
                        >
                          <div
                            className={`assessment-icon-circle ${
                              employee.mayamayaAssessment
                                .skillAssessmentCompleted
                                ? "completed"
                                : ""
                            }`}
                          >
                            <Award />
                          </div>
                          <span className="step-label">
                            {t("employees.assessment.skillProfile")}
                          </span>
                        </div>

                        {/* Line between Skill and Resume */}
                        <div
                          className={`progress-line ${
                            employee.mayamayaAssessment.skillAssessmentCompleted
                              ? "completed"
                              : ""
                          }`}
                        ></div>

                        {/* Resume Upload Step */}
                        <div
                          onClick={() =>
                            !employee.mayamayaAssessment.resumeUploadCompleted
                              ? handleStatusEmail("Resume", employee._id)
                              : null
                          }
                          className={`assessment-step-item ${
                            employee.mayamayaAssessment.resumeUploadCompleted
                              ? "completed"
                              : ""
                          }`}
                          title={
                            employee.mayamayaAssessment.resumeUploadCompleted
                              ? t("employees.resumeCompleted")
                              : t("employees.resumePending")
                          }
                        >
                          <div
                            className={`assessment-icon-circle ${
                              employee.mayamayaAssessment.resumeUploadCompleted
                                ? "completed"
                                : ""
                            }`}
                          >
                            <FileUp />
                          </div>
                          <span className="step-label">
                            {/* {employee.mayamayaAssessment.resumeUploadCompleted
                              ? "Completed"
                              : "Pending"} */}
                            Resume
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {showSharedWithColumn && (
                    <div className="list-col shared-with-cell">
                      {renderSharedWithCell(employee)}
                      {/* {getSharedWithText(employee) !== "Not shared" && (
                        <button
                          type="button"
                          className="remove-share-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            MessageManager.showToast(
                              "success",
                              "Profile share removed.",
                            );
                          }}
                        >
                          Remove Share
                        </button>
                      )} */}
                    </div>
                  )}
                  <div className="list-col">
                    <button
                      disabled={!peopleAccess.canDeleteUsers}
                      className="delete-btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEmployee(employee);
                      }}
                      title={
                        showDisabledDeletePeopleTooltip
                          ? permissionDeniedMessage
                          : t("employees.delete.removeEmployee")
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {peopleLength && (
            <Pagination
              loading={isLoading}
              total={peopleLength}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={filteredEmployees.length}
              onPageChange={setCurrentPage}
              pageSizeOptions={[20, 40, 60, 100]}
              pageWindowRadius={isMobile ? 1 : 2}
              pageJump={isMobile ? 10 : 5}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((peopleLength || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("mobilityPage.paginationInfo", { start, end, total })
              }
            />
          )}
        </div>
      </main>

      {/* Legacy invite popup disabled. Active popup is below. */}
      {false && showInvitePopup && (
        <div
          className="invite-popup-employees"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-modal-title"
          aria-describedby="invite-modal-desc"
        >
          <div className="invite-popup-content-employees">
            {/* Accent & Close */}
            <div className="invite-modal-accent" aria-hidden="true" />
            <button
              className="invite-close-btn"
              type="button"
              aria-label={t("employees.invite.close")}
              onClick={() => {
                setShowInvitePopup(false);
                setShowInviteLinkCopied(false);
                setShowInviteLink("");
                setInvitePeopleDepartment("");
              }}
            >
              ×
            </button>

            {/* Header */}
            <h3 id="invite-modal-title">{t("employees.invite.inviteTitle")}</h3>
            <p id="invite-modal-desc" className="invite-subtitle">
              Pick a department to generate a secure invite link
            </p>

            {/* Step 1: Department */}
            <div className="invite-section">
              <div className="ifilter-group">
                <label className="invite-label">
                  {t("employees.filters.department")}
                </label>
                <div className="iselect-wrapper">
                  <select
                    value={invitePeopleDepartment}
                    onChange={(e) => {
                      const value = e.target.value;

                      // If user selects placeholder again -> hide Step 2
                      if (value === "") {
                        setInvitePeopleDepartment("");
                        setShowInviteLinkCopied(false);
                        setShowInviteLink("");
                        setCopied(false);
                        return;
                      }

                      if (value === "__add__") {
                        navigator("/settings/organization-info");
                        return;
                      }

                      // Switching department -> hide old link while fetching new one
                      setShowInviteLinkCopied(false);
                      setShowInviteLink("");
                      setCopied(false);

                      setInvitePeopleDepartment(value);
                    }}
                    className="ifilter-select"
                  >
                    {inviteDepartments.length === 0 ? (
                      <>
                        <option value="">
                          {t("employees.filters.noDepartmentsAvailable")}
                        </option>
                        <option value="__add__">
                          {t("employees.filters.createDepartment")}
                        </option>
                      </>
                    ) : (
                      <>
                        <option value="">
                          {t("employees.filters.selectDepartment")}
                        </option>
                        {inviteDepartments.map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                        <option value="__add__">Create Department</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="iselect-hint">
                  Can’t find it? Choose “Create Department”.
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="invite-divider" aria-hidden="true" />

            {/* Step 2: Share */}
            {showInviteLinkCopied && (
              <div className="invite-section">
                <p className="invite-label">
                  {t("employees.invite.shareLink")}
                </p>

                <div className="invite-link-container-employees">
                  <input
                    type="text"
                    value={showInviteLink}
                    readOnly
                    className="invite-link-input-employees"
                    aria-label={t("employees.invite.generatedInviteLink")}
                  />

                  <button
                    className={`copy-btn-employees ${
                      copied ? "is-copied" : ""
                    }`}
                    onClick={handleCopyInviteLink}
                    disabled={!showInviteLink}
                    aria-live="polite"
                    type="button"
                  >
                    {copied ? (
                      <>
                        <Check size={16} />
                        {t("employees.invite.copied")}
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        {t("employees.invite.copy")}
                      </>
                    )}
                  </button>
                </div>

                <div className="invite-tip">
                  This link is unique to the selected department.
                </div>
              </div>
            )}

            {/* Footer */}
            {/*<div className="invite-footer">
        <button
          className="close-popup-btn-employees"
          type="button"
          onClick={() => {
            setShowInvitePopup(false);
            setShowInviteLinkCopied(false);
            setShowInviteLink("");
            setInvitePeopleDepartment("");
          }}
        >
          {t("employees.invite.close")}
        </button>
      </div>*/}
          </div>
        </div>
      )}

      {/* View More Modal */}
      <ViewMoreModal
        open={show}
        onClose={() => setShow(false)}
        title={ViewMoreModalTitle}
        skills={selectedSkills}
        variant="job-skills"
        type="hard"
        onRemoveSkill={(skill) =>
          setSelectedSkills((s) => s.filter((x) => x !== skill))
        }
      />

      {/* **************************** */}
      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            employeeId: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false}
      >
        {showModal.showButtons && (
          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                  employeeId: null,
                })
              }
            >
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary" onClick={showModal.action}>
              {t("employees.delete.removeEmployee")}
            </button>
          </div>
        )}
      </Modal>

      {/* *************************BULK DEPARTMENT EDIT ************************* */}
      {/* Invite Popup */}
      {showBulkDepartmentEdit && (
        // {showInvitePopup && (
        <div
          className="invite-popup-employees"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-modal-title"
          aria-describedby="invite-modal-desc"
        >
          <div className="invite-popup-content-employees">
            {/* Accent & Close */}
            <div className="invite-modal-accent" aria-hidden="true" />
            <button
              className="invite-close-btn"
              type="button"
              aria-label={t("employees.invite.close")}
              onClick={() => {
                setShowBulkDepartmentEdit(false);
                // setShowInviteLinkCopied(false);
                // setShowInviteLink("");
                // setInvitePeopleDepartment("");
              }}
            >
              ×
            </button>

            {/* Header */}
            <h3 id="invite-modal-title">{t("employees.bulkEdit.title")}</h3>
            <p id="invite-modal-desc" className="invite-subtitle">
              {t("employees.bulkEdit.description")}
            </p>

            {/* Step 1: Department */}
            <div className="invite-section">
              <div className="ifilter-group">
                <label className="invite-label">
                  {t("employees.filters.department")}
                </label>
                <div className="iselect-wrapper">
                  <select
                    value={bulkEditDepartment}
                    onChange={(e) => {
                      if (e.target.value === "__add__") {
                        navigator("/settings/organization-info");
                        return;
                      }
                      // setInvitePeopleDepartment(e.target.value);
                      setBulkEditDepartment(e.target.value);
                    }}
                    className="ifilter-select"
                  >
                    {departmentsList.length === 0 ? (
                      <>
                        <option value="">
                          {t("employees.filters.noDepartmentsAvailable")}
                        </option>
                        <option value="__add__">
                          {t("employees.filters.createDepartment")}
                        </option>
                      </>
                    ) : (
                      <>
                        <option value="">
                          {t("employees.filters.selectDepartment")}
                        </option>
                        {departmentsList.map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                {departmentsList.length == 0 && (
                  <div className="iselect-hint">
                    Can’t find any department? Choose “Create Department”.
                  </div>
                )}
              </div>
            </div>

            {/* Save button */}
            <div
              className="invite-modal-footer"
              style={{ float: "right", marginTop: "1rem" }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={bulkEditDepartmentApiHandler}
                disabled={
                  isBulkSaving || !bulkEditDepartment || !selectedIds.length
                }
                aria-busy={isBulkSaving ? "true" : "false"}
              >
                {isBulkSaving && (
                  <span className="btn-spinner" aria-hidden="true" />
                )}
                <span>
                  {isBulkSaving ? t("common.saving") : t("common.save")}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {sharePartnerModalOpen && (
        <div
          className="mmh-share-partner-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-share-partner-title"
          onClick={() => {
            setSharePartnerModalOpen(false);
            setSharePartnerSearch("");
            setSharePartnerSelection("");
            setShowSharePartnerResults(false);
          }}
        >
          <div
            className="mmh-share-partner-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="mmh-share-partner-header">
              <div className="mmh-share-partner-heading">
                <span className="mmh-share-partner-heading-icon">
                  <Building2 size={20} />
                </span>

                <div>
                  <h2 id="profile-share-partner-title">Share with Partner</h2>

                  <p>
                    Select one connected organization to receive the chosen
                    profiles.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="mmh-share-partner-close"
                aria-label="Close"
                onClick={() => {
                  setSharePartnerModalOpen(false);
                  setSharePartnerSearch("");
                  setSharePartnerSelection("");
                  setShowSharePartnerResults(false);
                }}
              >
                <X size={19} />
              </button>
            </header>

            <div className="mmh-share-partner-body">
              <div className="mmh-share-partner-summary">
                <span>
                  <User size={17} />
                </span>

                <div>
                  <strong>
                    {sharePartnerProfileIds.length} profile
                    {sharePartnerProfileIds.length === 1 ? "" : "s"} selected
                  </strong>

                  <small>
                    Sharing access can be changed later from Partner Settings.
                  </small>
                </div>
              </div>

              <label className="mmh-share-partner-field-label">
                Partner organization
              </label>

              <div
                className="mmh-share-partner-picker"
                ref={sharePartnerPickerRef}
              >
                <div className="mmh-share-partner-control">
                  <Search size={17} />

                  <input
                    type="text"
                    value={sharePartnerSearch}
                    placeholder="Search organization, code or admin"
                    onFocus={() => setShowSharePartnerResults(true)}
                    onChange={(event) => {
                      const value = event.target.value;

                      setSharePartnerSearch(value);
                      setShowSharePartnerResults(true);

                      if (!value) {
                        setSharePartnerSelection("");
                      }
                    }}
                    autoComplete="off"
                  />

                  {(sharePartnerSearch || sharePartnerSelection) && (
                    <button
                      type="button"
                      aria-label="Clear selected partner"
                      onClick={() => {
                        setSharePartnerSearch("");
                        setSharePartnerSelection("");
                        setShowSharePartnerResults(true);
                      }}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>

                {showSharePartnerResults && (
                  <div className="mmh-share-partner-results">
                    {isProfileSharePartnersLoading ? (
                      <div className="mmh-share-partner-empty">
                        <Building2 size={18} />

                        <strong>Loading partner organizations...</strong>

                        <span>
                          Please wait while connected partners are loaded.
                        </span>
                      </div>
                    ) : profileSharePartnersError ? (
                      <div className="mmh-share-partner-empty">
                        <Info size={18} />

                        <strong>Unable to load organizations</strong>

                        <span>{profileSharePartnersError}</span>

                        <button
                          type="button"
                          onClick={loadEligibleProfileSharePartners}
                        >
                          Try again
                        </button>
                      </div>
                    ) : filteredProfileSharePartners.length > 0 ? (
                      filteredProfileSharePartners.map((partner) => {
                        const isSelected = sharePartnerSelection === partner.id;

                        return (
                          <button
                            key={partner.id}
                            type="button"
                            className={`mmh-share-partner-option ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() => {
                              setSharePartnerSelection(partner.id);

                              setSharePartnerSearch(partner.name);

                              setShowSharePartnerResults(false);
                            }}
                          >
                            <span className="mmh-share-partner-option-icon">
                              <Building2 size={17} />
                            </span>

                            <span className="mmh-share-partner-option-copy">
                              <strong>{partner.name}</strong>

                              <small>
                                {[partner.orgCode, partner.admin]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </small>

                              {partner.email && <small>{partner.email}</small>}
                            </span>

                            {isSelected && (
                              <Check
                                size={17}
                                className="mmh-share-partner-option-check"
                              />
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="mmh-share-partner-empty">
                        <Search size={18} />

                        <strong>
                          {sharePartnerSearch.trim()
                            ? "No matching organization"
                            : "No eligible partner organizations"}
                        </strong>

                        <span>
                          {sharePartnerSearch.trim()
                            ? "Try searching by organization name, code, admin, or email."
                            : "Only active partners with Profiles sharing enabled are shown."}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedProfileSharePartner && !showSharePartnerResults && (
                <div className="mmh-share-partner-selected">
                  <span>
                    <Check size={16} />
                  </span>

                  <div>
                    <strong>{selectedProfileSharePartner.name}</strong>

                    <small>
                      {selectedProfileSharePartner.orgCode}
                      {selectedProfileSharePartner.email
                        ? ` \u00B7 ${selectedProfileSharePartner.email}`
                        : ""}
                    </small>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowSharePartnerResults(true)}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>

            <footer className="mmh-share-partner-footer">
              <button
                type="button"
                className="mmh-share-partner-btn secondary"
                onClick={() => {
                  setSharePartnerModalOpen(false);
                  setSharePartnerSearch("");
                  setSharePartnerSelection("");
                  setShowSharePartnerResults(false);
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className="mmh-share-partner-btn primary"
                disabled={!sharePartnerSelection}
                // onClick={() => {
                //   MessageManager.showToast(
                //     "success",
                //     "Profiles shared with partner.",
                //   );

                //   setSharePartnerModalOpen(false);
                //   setSharePartnerSearch("");
                //   setSharePartnerSelection("");
                //   setShowSharePartnerResults(false);
                //   setSharePartnerProfileIds([]);
                //   deselect(sharePartnerProfileIds);
                // }}
                onClick={() =>
                  shareWithPartnersHandler(
                    sharePartnerSelection,
                    sharePartnerProfileIds,
                  )
                }
              >
                Share Profiles
              </button>
            </footer>
          </div>
        </div>
      )}

      {sharedPartnersModal.partners.length > 2 && (
        <div
          className="mmh-share-partner-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shared-partners-modal-title"
          onClick={closeSharedPartnersModal}
        >
          <div
            className="mmh-share-partner-modal mmh-shared-partners-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="mmh-share-partner-header">
              <div className="mmh-share-partner-heading">
                <span className="mmh-share-partner-heading-icon">
                  <Building2 size={20} />
                </span>

                <div>
                  <h2 id="shared-partners-modal-title">Shared With</h2>
                  <p>
                    {sharedPartnersModal.employeeName} is shared with{" "}
                    {sharedPartnersModal.partners.length} partner
                    {sharedPartnersModal.partners.length === 1 ? "" : "s"}.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="mmh-share-partner-close"
                aria-label="Close"
                onClick={closeSharedPartnersModal}
              >
                <X size={19} />
              </button>
            </header>

            <div className="mmh-share-partner-body">
              <div className="mmh-shared-partners-copy">
                {sharedPartnersModal.partners.join(", ")}
              </div>

              <div className="mmh-shared-partners-list">
                {sharedPartnersModal.partners.map((partner) => (
                  <div key={partner} className="mmh-shared-partner-row">
                    <span>
                      <Building2 size={16} />
                    </span>
                    <strong>{partner}</strong>
                  </div>
                ))}
              </div>
            </div>

            <footer className="mmh-share-partner-footer">
              <button
                type="button"
                className="mmh-share-partner-btn secondary"
                onClick={closeSharedPartnersModal}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />

      {/* *************************INVITE POPUP ************************* */}
      {showInvitePopup && (
        <div
          className="invite-popup-employees"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-modal-title"
          aria-describedby="invite-modal-desc"
        >
          <div className="invite-popup-content-employees">
            {/* Accent & Close */}
            <div className="invite-modal-accent" aria-hidden="true" />
            <button
              className="invite-close-btn"
              type="button"
              aria-label={t("employees.invite.close")}
              onClick={() => {
                setShowInvitePopup(false);
                setShowInviteLinkCopied(false);
                setShowInviteLink("");
                setInvitePeopleDepartment("");
                setInviteUserType("");
                setInviteEducationLevel("");
                setInviteFieldOfStudy("");
                setInviteSection("");
                setInvitePartner("");

                setPartnerInviteMessage("");
                setPartnerInvitePrepared(false);
              }}
            >
              ×
            </button>

            {/* Header */}
            <h3 id="invite-modal-title">{t("employees.invite.inviteTitle")}</h3>

            <p id="invite-modal-desc" className="invite-subtitle">
              {t("employees.invite.subtitleFull")}
            </p>

            {/* Step 1: User Type */}
            <div className="invite-section">
              <p className="invite-label">
                {t("employees.invite.userType")}{" "}
                <span style={{ color: "red" }}>*</span>
              </p>

              <select
                value={inviteUserType}
                onChange={(e) => {
                  setInviteUserType(e.target.value);
                  setInviteEducationLevel(""); // reset education level when type changes
                  setInviteFieldOfStudy(""); // reset field of study when type changes
                  setInvitePartner("");

                  setPartnerInviteMessage("");
                  setPartnerInvitePrepared(false);
                  setShowInviteLink("");
                  setShowInviteLinkCopied(false);
                }}
                className="ifilter-select"
              >
                <option value="">{t("employees.invite.selectUserType")}</option>
                <option value="Student">{t("employees.invite.student")}</option>
                <option value="Professional">
                  {t("employees.invite.professional")}
                </option>
              </select>
            </div>

            {/* EXTERNAL CANDIDATE FLOW */}
            {inviteUserType === "External Candidate" && (
              <>
                <div className="invite-section mmh-external-vendor-section">
                  <div className="mmh-external-vendor-notice">
                    <ShieldCheck
                      size={14}
                      style={{ flexShrink: 0, marginTop: "1px" }}
                    />
                    <span>
                      External candidates remain protected. Do not collect or
                      display candidate name, email, phone, resume, LinkedIn,
                      portfolio, or personal contact details inside this
                      workspace.
                    </span>
                  </div>
                  <label className="invite-label">
                    Select Partner <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    value={invitePartner}
                    onChange={(e) => {
                      setInvitePartner(e.target.value);
                      setShowInviteLink("");
                      setShowInviteLinkCopied(false);
                      setPartnerInviteMessage("");
                      setPartnerInvitePrepared(false);
                    }}
                    className="ifilter-select"
                  >
                    <option value="">Select a partner</option>
                    <option value="Apex Talent Partners">
                      Apex Talent Partners
                    </option>
                    <option value="GlobalStaff Solutions">
                      GlobalStaff Solutions
                    </option>
                    <option value="NorthBridge Talent">
                      NorthBridge Talent
                    </option>
                  </select>
                  <p className="invite-tip">
                    The invite link will be partner-associated. Candidate
                    details remain protected.
                  </p>
                </div>
              </>
            )}

            {/* PROFESSIONAL FLOW */}
            {inviteUserType === "Professional" && (
              <>
                {/* Department */}
                <div className="invite-section">
                  <label className="invite-label">
                    {t("employees.filters.department")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>

                  <select
                    value={invitePeopleDepartment}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "") {
                        setInvitePeopleDepartment("");
                        return;
                      }

                      setInvitePeopleDepartment(value);
                    }}
                    className="ifilter-select"
                  >
                    <option value="">
                      {t("employees.filters.selectDepartment")}
                    </option>
                    {inviteDepartments.map((dept) => (
                      <option key={dept._id} value={dept.name}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* STUDENT FLOW */}
            {inviteUserType === "Student" && (
              <>
                <div className="invite-section">
                  <label className="invite-label">
                    {t("employees.filters.department")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>

                  <select
                    value={invitePeopleDepartment}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "") {
                        setInvitePeopleDepartment("");
                        return;
                      }

                      setInvitePeopleDepartment(value);
                    }}
                    className="ifilter-select"
                  >
                    <option value="">
                      {t("employees.filters.selectDepartment")}
                    </option>
                    {inviteDepartments.map((dept) => (
                      <option key={dept._id} value={dept.name}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="invite-section">
                  <p className="invite-label">
                    {t("employees.invite.educationLevel")} education
                    {/* education field */}
                    <span style={{ color: "red" }}>*</span>
                  </p>

                  <select
                    value={inviteEducationLevel}
                    onChange={(e) => {
                      setInviteEducationLevel(e.target.value);
                      setInviteFieldOfStudy(""); // reset field of study when education level changes
                    }}
                    className="ifilter-select"
                  >
                    <option value="">Select Education Level</option>
                    {educationLevelOptions.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Field of Study - Required only for College/University */}
                <div className="invite-section">
                  <p className="invite-label">
                    {t("employees.invite.fieldOfStudy")}{" "}
                    {isInviteFieldOfStudyRequired && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </p>

                  <select
                    value={inviteFieldOfStudy}
                    onChange={(e) => setInviteFieldOfStudy(e.target.value)}
                    className="ifilter-select"
                  >
                    <option value="">
                      {t("employees.invite.selectFieldOfStudy")}
                    </option>
                    {fieldOfStudyOptions.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="invite-section">
                  <p className="invite-label">
                    {t("employees.invite.sectionOptional")}
                  </p>

                  <input
                    type="text"
                    value={inviteSection}
                    onChange={(e) => setInviteSection(e.target.value)}
                    placeholder={t("employees.invite.sectionPlaceholder")}
                    className="invite-link-input-employees"
                  />
                </div>
              </>
            )}

            {/* Divider */}
            <div className="invite-divider" aria-hidden="true" />

            {/* GENERATE LINK BUTTON — all types */}
            <div style={{ width: "100%", textAlign: "center" }}>
              <button
                style={{ width: "100%", textAlign: "center" }}
                className={`copy-btn-employees ${copied ? "is-copied" : ""}`}
                onClick={getpeopleInviteLinkHandler}
                disabled={
                  isInviteLinkLoading ||
                  !inviteUserType ||
                  (inviteUserType === "External Candidate" && !invitePartner) ||
                  (inviteUserType !== "External Candidate" &&
                    !invitePeopleDepartment) ||
                  (inviteUserType === "Student" &&
                    (!inviteEducationLevel ||
                      (isInviteFieldOfStudyRequired && !inviteFieldOfStudy)))
                }
                aria-busy={isInviteLinkLoading ? "true" : "false"}
                aria-live="polite"
                type="button"
              >
                <span style={{ textAlign: "center", width: "100%" }}>
                  {isInviteLinkLoading && (
                    <span className="btn-spinner" aria-hidden="true" />
                  )}
                  {isInviteLinkLoading
                    ? t("employees.invite.generatingInviteLink")
                    : t("employees.invite.generateInviteLink")}
                </span>
              </button>
            </div>

            {/* NON-EXTERNAL — show link after generation */}
            {inviteUserType !== "External Candidate" &&
              showInviteLinkCopied && (
                <div className="invite-section">
                  <p className="invite-label">
                    {t("employees.invite.shareLink")}
                  </p>

                  <div className="invite-link-container-employees">
                    <input
                      type="text"
                      value={showInviteLink}
                      readOnly
                      className="invite-link-input-employees"
                      aria-label="Generated invite link"
                    />

                    <button
                      className={`copy-btn-employees ${copied ? "is-copied" : ""}`}
                      onClick={handleCopyInviteLink}
                      disabled={!showInviteLink}
                      aria-live="polite"
                      type="button"
                    >
                      {copied ? (
                        <>
                          <Check size={16} />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          {t("employees.invite.copy")}
                        </>
                      )}
                    </button>
                  </div>

                  <div className="invite-tip">
                    This link is unique to the selected department.
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;
